/**
 * TradingPlatform.js
 * 
 * ЗАМЕНЯЕТ: frontend/src/components/TradingDemo.js
 * 
 * В App.js заменить:
 *   import TradingDemo from './components/TradingDemo'
 *   → import TradingPlatform from './components/TradingPlatform'
 * 
 *   element={user ? <TradingDemo /> : <Navigate to="/" />}
 *   → element={user ? <TradingPlatform /> : <Navigate to="/" />}
 */

import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { AuthContext } from '../App';
import { calcMargin as fmCalcMargin, calcPnl as fmCalcPnl, targetPriceForUsd as fmTargetPriceForUsd } from '../utils/financeMath';
import { useTradingEngine } from './trading/TradingEngine';
import EconomicCalendar from './trading/EconomicCalendar';
import FundamentalAnalysis from './trading/FundamentalAnalysis';
import WithdrawModal from './trading/WithdrawModal';
import DepositModal from './trading/DepositModal';
import ClientMessages from './trading/ClientMessages';
// [TV-2026-05-18] TradingView Lightweight Charts — pro-grade chart layer
import LightweightCandleChart from './trading/LightweightCandleChart';
// [CFD-2026-05] Re-add modals from old TradingDemo so menu is feature-complete
import DocumentsModal from './trading/DocumentsModal';
import ChangePasswordModal from './trading/ChangePasswordModal';
import { getBackendUrl } from '../utils/api';
import { useLanguage } from '../contexts/LanguageContext';
// [CFD-2026-05] Redesign — flag SVGs + lucide icons
import * as Flags from 'country-flag-icons/react/3x2';
import {
  Wallet, Lock, TrendingUp, PieChart,
  LineChart, ClipboardList, Clock as ClockIcon, CalendarDays,
  Settings as SettingsIcon, ChevronLeft, ChevronRight, Bell, Sun, Moon,
  Crosshair, Minus as MinusIcon, Square as SquareIcon, Type as TypeIcon,
  TrendingDown, Ruler, ZoomIn, Eye, Trash2, Camera, Maximize2, ChevronDown
} from 'lucide-react';
import axios from 'axios';

const API = `${getBackendUrl()}/api`;

/* ─────────────────────────────────────────
   ASSET FLAG / ICON HELPERS
   ───────────────────────────────────────── */
// Map currency codes → ISO country codes for flag SVGs
const CURRENCY_CC = {
  EUR:'EU', USD:'US', GBP:'GB', JPY:'JP', AUD:'AU', CAD:'CA',
  CHF:'CH', NZD:'NZ', CNY:'CN', SEK:'SE', NOK:'NO', RUB:'RU',
};

// Crypto symbol → ticker for cryptocurrency-icons SVG file lookup
const CRYPTO_FILE = {
  BTCUSD:'btc', ETHUSD:'eth', BNBUSD:'bnb', XRPUSD:'xrp', SOLUSD:'sol',
  ADAUSD:'ada', DOTUSD:'dot', LINKUSD:'link', AVAXUSD:'avax', LTCUSD:'ltc',
  MATICUSD:'matic', ATOMUSD:'atom',
};

// Stock ticker → brand color for letter badge
const STOCK_COLOR = {
  AAPL:'#1d1d1f',  MSFT:'#0078d4', GOOGL:'#4285f4', AMZN:'#ff9900', NVDA:'#76b900',
  TSLA:'#cc0000',  META:'#1877f2', NFLX:'#e50914',  AMD:'#ed1c24',  JPM:'#0033a0',
};

// Index → country flag fallback
const INDEX_CC = {
  US30:'US', SPX500:'US', NAS100:'US', GER40:'DE', UK100:'GB',
  FRA40:'FR', JPN225:'JP', HK50:'HK',
};

// Commodity icon configs (color + emoji-like char fallback)
const COMMODITY_VIZ = {
  XAUUSD:{ c:'#fbbf24', t:'Au', g:'linear-gradient(135deg, #fef3c7, #d4af37)' },
  XAGUSD:{ c:'#9ca3af', t:'Ag', g:'linear-gradient(135deg, #f3f4f6, #9ca3af)' },
  USOIL: { c:'#1f2937', t:'WTI', g:'linear-gradient(135deg, #374151, #111827)' },
  UKOIL: { c:'#0d3b66', t:'BR',  g:'linear-gradient(135deg, #1e3a5f, #0a2540)' },
  NATGAS:{ c:'#3b82f6', t:'NG',  g:'linear-gradient(135deg, #60a5fa, #2563eb)' },
  COPPER:{ c:'#b87333', t:'Cu',  g:'linear-gradient(135deg, #d97740, #92400e)' },
  CORN:  { c:'#fbbf24', t:'Cn',  g:'linear-gradient(135deg, #fde68a, #d97706)' },
  WHEAT: { c:'#facc15', t:'Wh',  g:'linear-gradient(135deg, #fef08a, #ca8a04)' },
};

// ETF visualization (color + 2-letter)
const ETF_VIZ = {
  SPY:{c:'#dc2626',t:'SPY'}, QQQ:{c:'#2563eb',t:'QQQ'},
  GLD:{c:'#d4af37',t:'Au'},  SLV:{c:'#9ca3af',t:'Ag'},
  USO:{c:'#1f2937',t:'OIL'}, XLF:{c:'#059669',t:'Fin'}, XLE:{c:'#ea580c',t:'En'},
};

function FlagIcon({ symbol, size = 28 }) {
  // 1) Forex pairs: split two flags diagonally
  if (symbol && /^[A-Z]{6}$/.test(symbol) && !CRYPTO_FILE[symbol]) {
    const a = symbol.slice(0, 3), b = symbol.slice(3);
    const FA = Flags[CURRENCY_CC[a]] || null;
    const FB = Flags[CURRENCY_CC[b]] || null;
    if (FA && FB) {
      return (
        <div style={{ position:'relative', width:size, height:size, borderRadius:'50%', overflow:'hidden', flexShrink:0, boxShadow:'0 0 0 1px rgba(255,255,255,.06)' }}>
          <div style={{ position:'absolute', inset:0, clipPath:'polygon(0 0, 100% 0, 0 100%)' }}>
            <FA style={{ width:size, height:size, objectFit:'cover' }} />
          </div>
          <div style={{ position:'absolute', inset:0, clipPath:'polygon(100% 0, 100% 100%, 0 100%)' }}>
            <FB style={{ width:size, height:size, objectFit:'cover' }} />
          </div>
        </div>
      );
    }
  }
  // 2) Crypto — load real SVG from cryptocurrency-icons
  if (CRYPTO_FILE[symbol]) {
    const file = CRYPTO_FILE[symbol];
    return (
      <div style={{ width:size, height:size, borderRadius:'50%', overflow:'hidden', flexShrink:0, background:'#1a1f3a', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <img
          src={`/static/cryptos/${file}.svg`}
          alt={symbol}
          style={{ width:size, height:size, objectFit:'contain' }}
          onError={(e)=>{ e.target.style.display='none'; }}
        />
      </div>
    );
  }
  // 3) Index — use country flag
  if (INDEX_CC[symbol]) {
    const FA = Flags[INDEX_CC[symbol]];
    if (FA) {
      return (
        <div style={{ width:size, height:size, borderRadius:'50%', overflow:'hidden', flexShrink:0, position:'relative', boxShadow:'0 0 0 1px rgba(255,255,255,.06)' }}>
          <FA style={{ width:size, height:size, objectFit:'cover' }} />
        </div>
      );
    }
  }
  // 4) Commodity — gradient circle with text
  if (COMMODITY_VIZ[symbol]) {
    const v = COMMODITY_VIZ[symbol];
    return (
      <div style={{ width:size, height:size, borderRadius:'50%', background:v.g, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize: size>=28?10:8, fontWeight:800, flexShrink:0, textShadow:'0 1px 2px rgba(0,0,0,.3)' }}>
        {v.t}
      </div>
    );
  }
  // 5) Stock — colored circle with first letter (brand colors)
  if (STOCK_COLOR[symbol]) {
    return (
      <div style={{ width:size, height:size, borderRadius:'50%', background:STOCK_COLOR[symbol], display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize: size>=28?13:10, fontWeight:800, flexShrink:0 }}>
        {symbol[0]}
      </div>
    );
  }
  // 6) ETF — colored circle with code
  if (ETF_VIZ[symbol]) {
    const v = ETF_VIZ[symbol];
    return (
      <div style={{ width:size, height:size, borderRadius:'50%', background:v.c, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize: size>=28?10:8, fontWeight:800, flexShrink:0 }}>
        {v.t}
      </div>
    );
  }
  // Default fallback
  return (
    <div style={{ width:size, height:size, borderRadius:'50%', background:'#6366f1', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize: size>=28?11:9, fontWeight:800, flexShrink:0, letterSpacing:.2 }}>
      {(symbol||'?').slice(0,3)}
    </div>
  );
}

/* ─────────────────────────────────────────
   MARKETS DATA
   ───────────────────────────────────────── */
const MKT = {
  forex: {
    label: 'Форекс', lev: 100,
    syms: [
      { s:'EURUSD', l:'EUR/USD', p:1.171,   sp:.0003, d:5 },
      { s:'GBPUSD', l:'GBP/USD', p:1.268,   sp:.0004, d:5 },
      { s:'USDJPY', l:'USD/JPY', p:157.8,   sp:.03,   d:3 },
      { s:'AUDUSD', l:'AUD/USD', p:.6685,   sp:.0004, d:5 },
      { s:'USDCAD', l:'USD/CAD', p:1.369,   sp:.0004, d:5 },
      { s:'USDCHF', l:'USD/CHF', p:.876,    sp:.0004, d:5 },
      { s:'NZDUSD', l:'NZD/USD', p:.605,    sp:.0006, d:5 },
      { s:'EURGBP', l:'EUR/GBP', p:.923,    sp:.0003, d:5 },
      { s:'EURJPY', l:'EUR/JPY', p:184.7,   sp:.04,   d:3 },
      { s:'GBPJPY', l:'GBP/JPY', p:200.1,   sp:.06,   d:3 },
    ]
  },
  crypto: {
    label: 'Крипто', lev: 20,
    syms: [
      { s:'BTCUSD',   l:'Bitcoin',   p:67420,  sp:15,    d:1 },
      { s:'ETHUSD',   l:'Ethereum',  p:3512,   sp:2,     d:2 },
      { s:'BNBUSD',   l:'BNB',       p:602,    sp:.5,    d:2 },
      { s:'XRPUSD',   l:'Ripple',    p:.5234,  sp:.0005, d:4 },
      { s:'SOLUSD',   l:'Solana',    p:172.4,  sp:.05,   d:2 },
      { s:'ADAUSD',   l:'Cardano',   p:.4521,  sp:.0005, d:4 },
      { s:'DOTUSD',   l:'Polkadot',  p:7.82,   sp:.01,   d:3 },
      { s:'LINKUSD',  l:'Chainlink', p:14.32,  sp:.02,   d:3 },
      { s:'AVAXUSD',  l:'Avalanche', p:36.42,  sp:.05,   d:3 },
      { s:'LTCUSD',   l:'Litecoin',  p:84.2,   sp:.1,    d:2 },
      { s:'MATICUSD', l:'Polygon',   p:.7123,  sp:.001,  d:4 },
      { s:'ATOMUSD',  l:'Cosmos',    p:8.94,   sp:.01,   d:3 },
    ]
  },
  commodities: {
    label: 'Сырьё', lev: 50,
    syms: [
      { s:'XAUUSD', l:'Золото',      p:2318.5, sp:.5,   d:2 },
      { s:'XAGUSD', l:'Серебро',     p:27.42,  sp:.05,  d:3 },
      { s:'USOIL',  l:'Нефть WTI',   p:82.4,   sp:.05,  d:2 },
      { s:'UKOIL',  l:'Нефть Brent', p:86.2,   sp:.06,  d:2 },
      { s:'NATGAS', l:'Газ',         p:2.142,  sp:.003, d:3 },
      { s:'COPPER', l:'Медь',        p:4.524,  sp:.004, d:3 },
      { s:'CORN',   l:'Кукуруза',    p:442.5,  sp:.5,   d:1 },
      { s:'WHEAT',  l:'Пшеница',     p:538,    sp:.5,   d:1 },
    ]
  },
  indices: {
    label: 'Индексы', lev: 100,
    syms: [
      { s:'US30',   l:'Dow Jones',  p:38924, sp:3,  d:1 },
      { s:'SPX500', l:'S&P 500',    p:5248,  sp:.5, d:1 },
      { s:'NAS100', l:'NASDAQ',     p:18340, sp:1,  d:1 },
      { s:'GER40',  l:'DAX 40',     p:18342, sp:2,  d:1 },
      { s:'UK100',  l:'FTSE 100',   p:8340,  sp:1,  d:1 },
      { s:'FRA40',  l:'CAC 40',     p:8124,  sp:1,  d:1 },
      { s:'JPN225', l:'Nikkei',     p:38820, sp:5,  d:0 },
      { s:'HK50',   l:'Hang Seng',  p:17240, sp:5,  d:0 },
    ]
  },
  stocks: {
    label: 'Акции', lev: 10,
    syms: [
      { s:'AAPL',  l:'Apple',        p:294.0, sp:.05,  d:2 },
      { s:'MSFT',  l:'Microsoft',    p:475.5, sp:.05,  d:2 },
      { s:'GOOGL', l:'Alphabet',     p:215.2, sp:.1,   d:2 },
      { s:'AMZN',  l:'Amazon',       p:235.4, sp:.1,   d:2 },
      { s:'NVDA',  l:'NVIDIA',       p:178.4, sp:.1,   d:2 },
      { s:'TSLA',  l:'Tesla',        p:412.8, sp:.1,   d:2 },
      { s:'META',  l:'Meta',         p:712.3, sp:.1,   d:2 },
      { s:'NFLX',  l:'Netflix',      p:1027.4,sp:.15,  d:2 },
      { s:'AMD',   l:'AMD',          p:162.3, sp:.1,   d:2 },
      { s:'JPM',   l:'JPMorgan',     p:286.5, sp:.08,  d:2 },
      // [STK-2026-05-14] User-requested CIS-friendly stocks
      { s:'KSPI',  l:'Kaspi.kz',     p:86.4,  sp:.1,   d:2 },
      { s:'CVX',   l:'Chevron',      p:186.0, sp:.05,  d:2 },
      { s:'KAP',   l:'Казатомпром',  p:73.5,  sp:.1,   d:2 },
    ]
  },
  etf: {
    label: 'ETF', lev: 10,
    syms: [
      { s:'SPY', l:'SPY ETF',     p:524.3, sp:.05, d:2 },
      { s:'QQQ', l:'QQQ ETF',     p:446.2, sp:.05, d:2 },
      { s:'GLD', l:'Gold ETF',    p:218.4, sp:.05, d:2 },
      { s:'SLV', l:'Silver ETF',  p:24.32, sp:.03, d:2 },
      { s:'USO', l:'Oil ETF',     p:73.4,  sp:.05, d:2 },
      { s:'XLF', l:'Finance ETF', p:41.2,  sp:.03, d:2 },
      { s:'XLE', l:'Energy ETF',  p:88.4,  sp:.05, d:2 },
    ]
  },
};

const BASE_PRICES = {};
Object.values(MKT).forEach(m => m.syms.forEach(s => { BASE_PRICES[s.s] = s.p; }));

/* ─────────────────────────────────────────
   QUOTES HOOK — Finnhub WS + simulation fallback
   Замените REACT_APP_FINNHUB_KEY в .env для реальных котировок
   ───────────────────────────────────────── */
const FINNHUB_MAP = {
  EURUSD:'OANDA:EUR_USD', GBPUSD:'OANDA:GBP_USD', USDJPY:'OANDA:USD_JPY',
  AUDUSD:'OANDA:AUD_USD', USDCAD:'OANDA:USD_CAD', USDCHF:'OANDA:USD_CHF',
  NZDUSD:'OANDA:NZD_USD', EURGBP:'OANDA:EUR_GBP', EURJPY:'OANDA:EUR_JPY',
  GBPJPY:'OANDA:GBP_JPY', BTCUSD:'BINANCE:BTCUSDT', ETHUSD:'BINANCE:ETHUSDT',
  BNBUSD:'BINANCE:BNBUSDT', XRPUSD:'BINANCE:XRPUSDT', SOLUSD:'BINANCE:SOLUSDT',
  ADAUSD:'BINANCE:ADAUSDT', DOTUSD:'BINANCE:DOTUSDT', LINKUSD:'BINANCE:LINKUSDT',
  AVAXUSD:'BINANCE:AVAXUSDT', LTCUSD:'BINANCE:LTCUSDT', MATICUSD:'BINANCE:MATICUSDT',
  ATOMUSD:'BINANCE:ATOMUSDT', XAUUSD:'OANDA:XAU_USD', XAGUSD:'OANDA:XAG_USD',
  USOIL:'OANDA:BCO_USD',
};

// [CFD-2026-05] Stocks/ETF — Finnhub free plan: REST /quote endpoint (no WS)
const FINNHUB_REST_SYMS = ['AAPL','MSFT','GOOGL','AMZN','NVDA','TSLA','META','NFLX','AMD','JPM','SPY','QQQ','GLD','SLV','USO','XLF','XLE'];

function useQuotes() {
  const [prices, setPrices] = useState({ ...BASE_PRICES });
  // [MARKET-2026-05-15] Per-symbol market status ("open"/"closed") + a global message
  // so the UI can freeze quotes and grey out closed assets.
  const [marketStatus, setMarketStatus] = useState({});
  const [sessionCloseMsg, setSessionCloseMsg] = useState('');
  const wsRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const apiKey = process.env.REACT_APP_FINNHUB_API_KEY || process.env.REACT_APP_FINNHUB_KEY || ''; // [CFD-2026-05]
    if (!apiKey) {
      // Simulation mode
      timerRef.current = setInterval(() => {
        setPrices(prev => {
          const next = { ...prev };
          Object.values(MKT).forEach(m => m.syms.forEach(s => {
            next[s.s] = parseFloat((next[s.s] + (Math.random() - 0.49) * next[s.s] * 0.00018).toFixed(s.d));
          }));
          return next;
        });
      }, 1400);
      return () => clearInterval(timerRef.current);
    }
    // Real Finnhub WebSocket
    const allSyms = Object.values(MKT).flatMap(m => m.syms);
    const connect = () => {
      const ws = new WebSocket(`wss://ws.finnhub.io?token=${apiKey}`);
      wsRef.current = ws;
      ws.onopen = () => allSyms.forEach(s => {
        const fSym = FINNHUB_MAP[s.s];
        if (fSym) ws.send(JSON.stringify({ type: 'subscribe', symbol: fSym }));
      });
      ws.onmessage = e => {
        const data = JSON.parse(e.data);
        if (data.type === 'trade' && data.data) {
          const updates = {};
          data.data.forEach(tick => {
            const entry = Object.entries(FINNHUB_MAP).find(([, v]) => v === tick.s);
            if (entry) {
              const sym = allSyms.find(s => s.s === entry[0]);
              if (sym) updates[sym.s] = parseFloat(tick.p.toFixed(sym.d));
            }
          });
          if (Object.keys(updates).length) setPrices(prev => ({ ...prev, ...updates }));
        }
      };
      ws.onclose = () => { timerRef.current = setTimeout(connect, 3000); };
      ws.onerror = () => ws.close();
    };
    connect();

    // [CFD-2026-05] REST polling for stocks/ETF (Finnhub free WS doesn't include them)
    const pollStocks = async () => {
      try {
        const updates = {};
        await Promise.all(FINNHUB_REST_SYMS.map(async sym => {
          try {
            const r = await fetch(`https://finnhub.io/api/v1/quote?symbol=${sym}&token=${apiKey}`);
            const d = await r.json();
            if (d && d.c && d.c > 0) {
              const meta = allSyms.find(s => s.s === sym);
              updates[sym] = parseFloat(d.c.toFixed(meta ? meta.d : 2));
            }
          } catch (_) {}
        }));
        if (Object.keys(updates).length) setPrices(prev => ({ ...prev, ...updates }));
      } catch (_) {}
    };
    pollStocks();
    const stockInterval = setInterval(pollStocks, 10000); // 10s — free plan limit-friendly

    // [CFD-2026-05] Finnhub forex (free WebSocket) via REST quote fallback for accurate forex
    // Finnhub forex symbols use OANDA: prefix
    const FINNHUB_FX_MAP = {
      EURUSD:'OANDA:EUR_USD', GBPUSD:'OANDA:GBP_USD', USDJPY:'OANDA:USD_JPY',
      AUDUSD:'OANDA:AUD_USD', USDCAD:'OANDA:USD_CAD', USDCHF:'OANDA:USD_CHF',
      NZDUSD:'OANDA:NZD_USD', EURGBP:'OANDA:EUR_GBP', EURJPY:'OANDA:EUR_JPY', GBPJPY:'OANDA:GBP_JPY',
    };
    const FINNHUB_COMMOD_MAP = {
      XAUUSD:'OANDA:XAU_USD', XAGUSD:'OANDA:XAG_USD',
      USOIL: 'OANDA:WTICO_USD', UKOIL:'OANDA:BCO_USD', NATGAS:'OANDA:NATGAS_USD',
    };
    const FINNHUB_IDX_MAP = {
      US30:'^DJI', SPX500:'^GSPC', NAS100:'^IXIC',
      GER40:'^GDAXI', UK100:'^FTSE', FRA40:'^FCHI', JPN225:'^N225',
    };
    const pollFinnhubExtra = async () => {
      const symMap = { ...FINNHUB_FX_MAP, ...FINNHUB_COMMOD_MAP, ...FINNHUB_IDX_MAP };
      try {
        const updates = {};
        await Promise.all(Object.entries(symMap).map(async ([ourSym, finnSym]) => {
          try {
            const r = await fetch(`https://finnhub.io/api/v1/quote?symbol=${encodeURIComponent(finnSym)}&token=${apiKey}`);
            const d = await r.json();
            if (d && d.c && d.c > 0) {
              const meta = allSyms.find(s => s.s === ourSym);
              updates[ourSym] = parseFloat(d.c.toFixed(meta ? meta.d : 4));
            }
          } catch (_) {}
        }));
        if (Object.keys(updates).length) setPrices(prev => ({ ...prev, ...updates }));
      } catch (_) {}
    };
    pollFinnhubExtra();
    const fxInterval = setInterval(pollFinnhubExtra, 15000); // 15s — these are slower-moving

    // [CFD-2026-05] Binance public REST for accurate real-time crypto prices
    // (no API key needed; bypasses Finnhub free WS limitations)
    const BINANCE_MAP = {
      BTCUSD:'BTCUSDT', ETHUSD:'ETHUSDT', BNBUSD:'BNBUSDT', XRPUSD:'XRPUSDT',
      SOLUSD:'SOLUSDT', ADAUSD:'ADAUSDT', DOTUSD:'DOTUSDT', LINKUSD:'LINKUSDT',
      AVAXUSD:'AVAXUSDT', LTCUSD:'LTCUSDT', MATICUSD:'MATICUSDT', ATOMUSD:'ATOMUSDT',
    };
    const pollCrypto = async () => {
      try {
        const symbolsParam = encodeURIComponent(JSON.stringify(Object.values(BINANCE_MAP)));
        const r = await fetch(`https://api.binance.com/api/v3/ticker/price?symbols=${symbolsParam}`);
        if (!r.ok) return;
        const data = await r.json();
        const updates = {};
        data.forEach(item => {
          const ourSym = Object.entries(BINANCE_MAP).find(([, v]) => v === item.symbol)?.[0];
          if (ourSym) {
            const p = parseFloat(item.price);
            const meta = allSyms.find(s => s.s === ourSym);
            updates[ourSym] = parseFloat(p.toFixed(meta ? meta.d : 2));
          }
        });
        if (Object.keys(updates).length) setPrices(prev => ({ ...prev, ...updates }));
      } catch (_) {}
    };
    pollCrypto();
    const cryptoInterval = setInterval(pollCrypto, 5000); // 5s — Binance is generous

    // [LP-2026-05-14] BACKEND CANONICAL POLLER — single source of truth for all symbols.
    // This poller HAS PRIORITY: it overwrites whatever Finnhub/Binance set so the
    // platform never flickers (e.g., EURUSD never reverts to a stale 1.08).
    const backendSymList = Object.values(MKT).flatMap(m => m.syms.map(s => s.s)).join(',');
    const pollBackendCanonical = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const r = await fetch(`${backendUrl}/api/market/live-prices?symbols=${backendSymList}`);
        if (!r.ok) return;
        const data = await r.json();
        const incoming = data?.prices || {};
        const incomingStatus = data?.status || {};
        const updates = {};
        Object.entries(incoming).forEach(([sym, p]) => {
          if (p && Number(p) > 0) {
            const meta = allSyms.find(s => s.s === sym);
            // [MARKET-2026-05-15] If market is CLOSED, do NOT overwrite price.
            // Keeps the last-known quote frozen on screen until session reopens.
            if (incomingStatus[sym] === 'closed') return;
            updates[sym] = parseFloat(Number(p).toFixed(meta ? meta.d : 4));
          }
        });
        if (Object.keys(updates).length) setPrices(prev => ({ ...prev, ...updates }));
        if (Object.keys(incomingStatus).length) setMarketStatus(incomingStatus);
        if (typeof data?.session_close_msg === 'string') setSessionCloseMsg(data.session_close_msg);
      } catch (_) {}
    };
    pollBackendCanonical();
    const canonicalInterval = setInterval(pollBackendCanonical, 4000);

    return () => { wsRef.current?.close(); clearTimeout(timerRef.current); clearInterval(stockInterval); clearInterval(cryptoInterval); clearInterval(fxInterval); clearInterval(canonicalInterval); };
  }, []);

  return { prices, marketStatus, sessionCloseMsg };
}

/* ─────────────────────────────────────────
   CHART CANVAS COMPONENT
   ───────────────────────────────────────── */
function CandleChart({ sym, prices, mode, openPositions, indicators, draws, drawNow, onCanvasReady, isDark, tf, tool }) {
  const canvasRef = useRef(null);
  const candlesRef = useRef([]);
  const coordsRef = useRef({ hi: 1, lo: 0, H: 200, pad: { l:4, r:62, t:8, b:22 }, bw: 8, startIdx: 0, visibleCount: 50 });
  // [CFD-2026-05] View state — pan/zoom (mutable refs to avoid re-renders)
  const viewRef = useRef({ visibleCount: 50, endIdx: 120 }); // candles[endIdx-visibleCount .. endIdx-1] visible
  const dragRef = useRef({ active: false, startX: 0, startEnd: 0 });
  const displayPriceRef = useRef(null); // smoothed displayed price for "alive" feel
  const animRef = useRef(null);

  const TF_MS = { '1м':60_000, '5м':300_000, '15м':900_000, '1ч':3_600_000, '4ч':14_400_000, '1д':86_400_000, '1н':604_800_000 };

  // [CFD-2026-05] Hold latest draw fn in a ref so the long-lived animation loop
  // always invokes the up-to-date closure (with current indicators/draws/mode/etc.)
  const drawRef = useRef(() => {});

  const toY = useCallback((v) => {
    const { hi, lo, H, pad } = coordsRef.current;
    return pad.t + (H - pad.t - pad.b) * (1 - (v - lo) / (hi - lo || 1));
  }, []);

  const fromY = useCallback((y) => {
    const { hi, lo, H, pad } = coordsRef.current;
    const ch = H - pad.t - pad.b;
    return lo + (ch - (y - pad.t)) / ch * (hi - lo);
  }, []);

  // [CFD-2026-05] Generate candles when symbol OR timeframe changes (more history for panning)
  // Walk BACKWARD from live price so the last candle aligns with current market price.
  useEffect(() => {
    const tfMul = { '1м':.0008, '5м':.0018, '15м':.0035, '1ч':.008, '4ч':.018, '1д':.035, '1н':.07 }[tf] || .0018;
    const tfMs = TF_MS[tf] || 60_000;
    const baseP = prices[sym.s] || sym.p;
    const TOTAL = 240;
    const now = Date.now();
    // Build close prices walking backwards
    const closes = new Array(TOTAL);
    closes[TOTAL - 1] = baseP;
    for (let i = TOTAL - 2; i >= 0; i--) {
      const drift = (Math.random() - 0.5) * closes[i + 1] * tfMul;
      closes[i] = Math.max(closes[i + 1] * 0.5, closes[i + 1] - drift);
    }
    const candles = [];
    for (let i = 0; i < TOTAL; i++) {
      const o = i === 0 ? closes[0] * (1 - (Math.random() - 0.5) * tfMul * 0.5) : closes[i - 1];
      const c = closes[i];
      const wickRange = Math.abs(c - o) + Math.random() * c * tfMul * 0.4;
      candles.push({
        o,
        c,
        h: Math.max(o, c) + wickRange * 0.4,
        l: Math.min(o, c) - wickRange * 0.4,
        v: Math.round(1000 + Math.random() * 9000),
        t: now - (TOTAL - 1 - i) * tfMs
      });
    }
    candlesRef.current = candles;
    viewRef.current = { visibleCount: 50, endIdx: TOTAL };
    displayPriceRef.current = baseP;
    draw();
  }, [sym.s, tf]);

  // [CFD-2026-05] Smooth animation loop — interpolate displayed price toward target +
  // tiny shimmer so chart line feels "alive" between Finnhub REST polls
  useEffect(() => {
    let alive = true;
    const tick = () => {
      if (!alive) return;
      const candles = candlesRef.current;
      const target = prices[sym.s] || sym.p;
      if (candles.length && target) {
        if (displayPriceRef.current == null) displayPriceRef.current = target;
        const dp = displayPriceRef.current;
        // Lerp 12% per frame toward target + microscopic random shimmer
        const shimmer = (Math.random() - 0.5) * target * 0.00006;
        const np = dp + (target - dp) * 0.12 + shimmer;
        displayPriceRef.current = np;
        const last = candles[candles.length - 1];
        last.c = np;
        last.h = Math.max(last.h, np);
        last.l = Math.min(last.l, np);
        drawRef.current();
      }
      animRef.current = setTimeout(tick, 80); // ~12fps — smooth without burning CPU
    };
    animRef.current = setTimeout(tick, 80);
    return () => { alive = false; clearTimeout(animRef.current); };
  }, [sym.s]);

  useEffect(() => { draw(); }, [isDark, mode, indicators, draws, drawNow]);

  // Keep drawRef pointing at latest draw fn after every render
  drawRef.current = draw;

  // Expose coords for drawing tools
  useEffect(() => {
    if (onCanvasReady) onCanvasReady({ fromY, toY, canvasRef });
  }, [fromY, toY, onCanvasReady]);

  function sma(d, n) { return d.map((_,i) => i < n-1 ? null : d.slice(i-n+1,i+1).reduce((a,b)=>a+b,0)/n); }
  function ema(d, n) {
    const k = 2/(n+1); let pv = null;
    return d.map((v, i) => {
      if (i < n-1) return null;
      if (i === n-1) { pv = d.slice(0,n).reduce((a,b)=>a+b,0)/n; return pv; }
      pv = v*k + pv*(1-k); return pv;
    });
  }
  function bbands(d, n=20, m=2) {
    const ma = sma(d, n);
    return d.map((_,i) => {
      if (ma[i] === null) return null;
      const sl = d.slice(Math.max(0,i-n+1),i+1);
      const std = Math.sqrt(sl.reduce((a,b)=>a+(b-ma[i])**2,0)/sl.length);
      return { u: ma[i]+m*std, l: ma[i]-m*std, mid: ma[i] };
    });
  }

  function draw() {
    const cv = canvasRef.current;
    if (!cv) return;
    const area = cv.parentElement;
    const W = area.clientWidth || 440, H = area.clientHeight || 200;
    if (W < 20) return;
    const dpr = window.devicePixelRatio || 1;
    cv.width = W * dpr; cv.height = H * dpr;
    cv.style.width = W + 'px'; cv.style.height = H + 'px';
    const ctx = cv.getContext('2d'); ctx.scale(dpr, dpr);

    // Background
    ctx.fillStyle = isDark ? '#111520' : '#ffffff';
    ctx.fillRect(0, 0, W, H);

    const pad = { l:4, r:62, t:8, b:22 };
    const cw = W - pad.l - pad.r, ch = H - pad.t - pad.b;
    const all = candlesRef.current;
    if (!all.length) return;

    // [CFD-2026-05] Visible window from view state (pan/zoom)
    const v = viewRef.current;
    const total = all.length;
    const visibleCount = Math.max(8, Math.min(total, Math.round(v.visibleCount)));
    const endIdx = Math.max(visibleCount, Math.min(total + Math.floor(visibleCount * 0.2), v.endIdx));
    const startIdx = endIdx - visibleCount;
    // Slice clipped to actual array (right side may go beyond -> empty space for "future")
    const realStart = Math.max(0, Math.floor(startIdx));
    const realEnd = Math.min(total, Math.ceil(endIdx));
    const vis = all.slice(realStart, realEnd);
    if (!vis.length) return;
    const bw = cw / visibleCount;
    // X offset: where startIdx maps to in pixels (can be negative for partial off-screen)
    const xOffset = pad.l - (startIdx - realStart) * bw;

    let hi = Math.max(...vis.map(c => c.h));
    let lo = Math.min(...vis.map(c => c.l));
    // extend for BB
    if (indicators.bb) {
      const bbs = bbands(vis.map(c => c.c));
      bbs.forEach(b => { if (b) { hi = Math.max(hi, b.u); lo = Math.min(lo, b.l); }});
    }
    let rng = hi - lo || 1; hi += rng*.06; lo -= rng*.03; rng = hi - lo;
    coordsRef.current = { hi, lo, H, pad, bw, startIdx: realStart, visibleCount };

    const tY = v => pad.t + ch * (1 - (v - lo) / rng);
    const xAt = i => xOffset + i * bw; // i is index within `vis`

    // Grid
    const gc = isDark ? 'rgba(255,255,255,.04)' : 'rgba(0,0,0,.04)';
    const lc = isDark ? '#6b7899' : '#9ba3c4';
    for (let i = 0; i <= 5; i++) {
      const y = pad.t + (ch/5)*i;
      ctx.beginPath(); ctx.moveTo(pad.l, y); ctx.lineTo(W-pad.r, y);
      ctx.strokeStyle = gc; ctx.lineWidth = .5; ctx.stroke();
      ctx.fillStyle = lc; ctx.font = '9px monospace'; ctx.textAlign = 'left';
      ctx.fillText((hi-(rng/5)*i).toFixed(sym.d), W-pad.r+3, y+3);
    }
    // Vertical gridlines + time axis labels
    const tfMs = TF_MS[tf] || 60_000;
    // [CFD-2026-05] Density-aware label step: target ~70 px between labels
    // → if each candle is wide (zoomed in), label every candle; otherwise skip some
    const targetLabelPx = 70;
    const cwPerCandle = bw;
    const labelStep = Math.max(1, Math.round(targetLabelPx / Math.max(cwPerCandle, 1)));
    ctx.fillStyle = lc; ctx.font = '10px monospace'; ctx.textAlign = 'center';
    for (let i = 0; i < vis.length; i += labelStep) {
      const x = xAt(i) + bw/2;
      if (x < pad.l - 5 || x > W - pad.r + 5) continue;
      ctx.beginPath(); ctx.moveTo(x, pad.t); ctx.lineTo(x, H-pad.b);
      ctx.strokeStyle = gc; ctx.lineWidth = .5; ctx.stroke();
      // [CFD-2026-05] Time label — format adapts per timeframe
      const ts = vis[i].t;
      if (ts) {
        const d = new Date(ts);
        let lbl;
        if (tfMs >= 86_400_000) lbl = d.toLocaleDateString('ru-RU', { day:'2-digit', month:'2-digit' });
        else if (tfMs >= 3_600_000) lbl = d.getHours().toString().padStart(2,'0') + ':00';
        else lbl = d.getHours().toString().padStart(2,'0') + ':' + d.getMinutes().toString().padStart(2,'0');
        ctx.fillStyle = lc;
        ctx.fillText(lbl, x, H - 6);
      }
    }

    // BB
    if (indicators.bb) {
      const bbs = bbands(vis.map(c => c.c));
      const bc = isDark ? '#9ab5ff' : '#3b6ef8';
      ['u','l'].forEach(k => {
        ctx.beginPath(); let f = true;
        bbs.forEach((b,i) => { if (!b) return; const x = xAt(i)+bw/2; if(f){ctx.moveTo(x,tY(b[k]));f=false;}else ctx.lineTo(x,tY(b[k])); });
        ctx.strokeStyle = bc+'cc'; ctx.lineWidth = 1.4; ctx.stroke();
      });
      ctx.beginPath(); let f = true;
      bbs.forEach((b,i) => { if (!b) return; const x = xAt(i)+bw/2; if(f){ctx.moveTo(x,tY(b.mid));f=false;}else ctx.lineTo(x,tY(b.mid)); });
      ctx.strokeStyle = bc+'88'; ctx.lineWidth = 1.2; ctx.setLineDash([3,3]); ctx.stroke(); ctx.setLineDash([]);
    }
    // MA
    if (indicators.ma) {
      const m = sma(vis.map(c=>c.c), 14);
      ctx.beginPath(); let f = true;
      m.forEach((v,i) => { if(v===null)return; const x=xAt(i)+bw/2; if(f){ctx.moveTo(x,tY(v));f=false;}else ctx.lineTo(x,tY(v)); });
      ctx.strokeStyle = '#f59e0b'; ctx.lineWidth = 1.5; ctx.stroke();
    }
    // EMA
    if (indicators.ema) {
      const e = ema(vis.map(c=>c.c), 21);
      ctx.beginPath(); let f = true;
      e.forEach((v,i) => { if(v===null)return; const x=xAt(i)+bw/2; if(f){ctx.moveTo(x,tY(v));f=false;}else ctx.lineTo(x,tY(v)); });
      ctx.strokeStyle = '#a78bfa'; ctx.lineWidth = 1.5; ctx.stroke();
    }

    // Candles
    vis.forEach((c, i) => {
      const x = xAt(i), bull = c.c >= c.o;
      if (x + bw < pad.l - 2 || x > W - pad.r + 2) return;
      const col = bull ? (isDark?'#10d988':'#0ea56f') : (isDark?'#ff5252':'#e53e3e');
      ctx.beginPath(); ctx.moveTo(x+bw/2, tY(c.h)); ctx.lineTo(x+bw/2, tY(c.l));
      ctx.strokeStyle = col; ctx.lineWidth = .8; ctx.stroke();
      const top = Math.min(tY(c.o),tY(c.c)), bod = Math.abs(tY(c.c)-tY(c.o))||1;
      ctx.fillStyle = bull?(isDark?'#10d98844':'#0ea56f44'):(isDark?'#ff525244':'#e53e3e44');
      ctx.fillRect(x+1, top, Math.max(bw-2,1), bod);
      ctx.strokeStyle = col; ctx.lineWidth = .8; ctx.strokeRect(x+1, top, Math.max(bw-2,1), bod);
    });

    // Price line (use displayed/smoothed price)
    const cp = displayPriceRef.current ?? prices[sym.s] ?? sym.p;
    const py = tY(cp);
    const lc2 = mode==='cfd' ? (isDark?'#6b8fff':'#3b6ef8') : (isDark?'#10d988':'#0ea56f');
    ctx.beginPath(); ctx.setLineDash([3,4]);
    ctx.moveTo(pad.l, py); ctx.lineTo(W-pad.r, py);
    ctx.strokeStyle = lc2; ctx.lineWidth = 1.2; ctx.stroke(); ctx.setLineDash([]);
    // Price tag
    const tw=58,th=15,tx=W-pad.r+2,ty=Math.max(4,Math.min(H-th-2,py-th/2));
    ctx.fillStyle = mode==='cfd'?(isDark?'#0d1530':'#eef2ff'):(isDark?'#071f14':'#e6f7f1');
    ctx.beginPath(); ctx.roundRect(tx,ty,tw,th,3); ctx.fill();
    ctx.fillStyle = mode==='cfd'?(isDark?'#c5d3ff':'#1a3a8f'):(isDark?'#5fffc0':'#064d33');
    ctx.font = 'bold 9px monospace'; ctx.textAlign = 'left';
    ctx.fillText(cp.toFixed(sym.d), tx+3, ty+10);

    // Open position lines (entry price marked on chart)
    const usedY = [];
    openPositions.filter(p => p.sym === sym.s && p.entry != null).forEach(pos => {
      const ey = tY(pos.entry);
      const ib = pos.dir === 'b' || pos.dir === 'c';
      const lnc = ib ? (isDark?'#10d988':'#0ea56f') : (isDark?'#ff5252':'#e53e3e');
      ctx.beginPath(); ctx.setLineDash([5,4]);
      ctx.moveTo(pad.l, ey); ctx.lineTo(W-pad.r, ey);
      ctx.strokeStyle = lnc+'cc'; ctx.lineWidth = 1.5; ctx.stroke(); ctx.setLineDash([]);
      // Entry price tag (avoid overlap)
      const lw=62,lh=14,lx=W-pad.r+2;
      let ly = Math.max(4, Math.min(H-lh-2, ey-lh/2));
      while (usedY.some(u => Math.abs(u-ly) < lh+2)) ly += lh+2;
      usedY.push(ly);
      ctx.fillStyle = ib?(isDark?'#071f14':'#e6f7f1'):(isDark?'#200808':'#fef0f0');
      ctx.beginPath(); ctx.roundRect(lx,ly,lw,lh,3); ctx.fill();
      ctx.strokeStyle = lnc+'66'; ctx.lineWidth = 1; ctx.strokeRect(lx,ly,lw,lh);
      ctx.fillStyle = lnc; ctx.font = 'bold 8px monospace'; ctx.textAlign = 'left';
      ctx.fillText((ib?'▲':'▼')+' '+pos.entry.toFixed(sym.d), lx+3, ly+10);
    });

    // Drawings
    [...(draws||[]), ...(drawNow?[{...drawNow, preview:true}]:[])].forEach(d => paintDraw(ctx,d,tY,pad,W,H,sym,isDark));
  }

  useEffect(() => {
    const handleResize = () => drawRef.current();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // [CFD-2026-05] Pan & Zoom handlers
  const handleWheel = useCallback(e => {
    e.preventDefault();
    const cv = canvasRef.current;
    if (!cv) return;
    const rect = cv.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const W = rect.width;
    const pad = coordsRef.current.pad || { l:4, r:62 };
    const cw = W - pad.l - pad.r;
    if (cw <= 0) return;
    const v = viewRef.current;
    const total = candlesRef.current.length;
    const oldVisible = v.visibleCount;
    const oldStart = v.endIdx - oldVisible;
    const oldBw = cw / oldVisible;
    // Candle index at mouse cursor (anchor for zoom)
    const candleAtMouse = oldStart + (mouseX - pad.l) / oldBw;
    const factor = e.deltaY > 0 ? 1.18 : 1 / 1.18;
    const newVisible = Math.max(10, Math.min(total, oldVisible * factor));
    const newBw = cw / newVisible;
    const newStart = candleAtMouse - (mouseX - pad.l) / newBw;
    const maxEnd = total + Math.floor(newVisible * 0.2);
    let newEnd = newStart + newVisible;
    newEnd = Math.max(newVisible, Math.min(maxEnd, newEnd));
    v.visibleCount = newVisible;
    v.endIdx = newEnd;
    drawRef.current();
  }, []);

  const handleMouseDown = useCallback(e => {
    if (tool && tool !== 'cur') return; // let drawing tools handle non-cursor modes
    dragRef.current = { active: true, startX: e.clientX, startEnd: viewRef.current.endIdx };
  }, [tool]);

  const handleMouseMove = useCallback(e => {
    if (!dragRef.current.active) return;
    const cv = canvasRef.current;
    if (!cv) return;
    const rect = cv.getBoundingClientRect();
    const W = rect.width;
    const pad = coordsRef.current.pad || { l:4, r:62 };
    const cw = W - pad.l - pad.r;
    if (cw <= 0) return;
    const v = viewRef.current;
    const bw = cw / v.visibleCount;
    const dx = e.clientX - dragRef.current.startX;
    const dCandles = dx / bw;
    const total = candlesRef.current.length;
    const maxEnd = total + Math.floor(v.visibleCount * 0.2);
    let newEnd = dragRef.current.startEnd - dCandles;
    newEnd = Math.max(v.visibleCount, Math.min(maxEnd, newEnd));
    v.endIdx = newEnd;
    drawRef.current();
  }, []);

  const handleMouseUp = useCallback(() => {
    dragRef.current.active = false;
  }, []);

  // [CFD-2026-05] Touch handlers — pinch zoom + 1-finger pan for mobile
  const touchRef = useRef({ touches: [], baseDist: 0, baseVisible: 50, baseEnd: 0, lastX: 0 });
  const handleTouchStart = useCallback(e => {
    if (e.touches.length === 1) {
      touchRef.current = { ...touchRef.current, lastX: e.touches[0].clientX, baseEnd: viewRef.current.endIdx };
    } else if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      touchRef.current = {
        ...touchRef.current,
        baseDist: Math.hypot(dx, dy),
        baseVisible: viewRef.current.visibleCount,
      };
    }
  }, []);
  const handleTouchMove = useCallback(e => {
    e.preventDefault();
    if (e.touches.length === 1) {
      const cv = canvasRef.current;
      if (!cv) return;
      const rect = cv.getBoundingClientRect();
      const W = rect.width;
      const pad = coordsRef.current.pad || { l:4, r:62 };
      const cw = W - pad.l - pad.r;
      if (cw <= 0) return;
      const v = viewRef.current;
      const bw = cw / v.visibleCount;
      const dx = e.touches[0].clientX - touchRef.current.lastX;
      touchRef.current.lastX = e.touches[0].clientX;
      const total = candlesRef.current.length;
      const maxEnd = total + Math.floor(v.visibleCount * 0.2);
      let newEnd = v.endIdx - dx / bw;
      newEnd = Math.max(v.visibleCount, Math.min(maxEnd, newEnd));
      v.endIdx = newEnd;
      drawRef.current();
    } else if (e.touches.length === 2 && touchRef.current.baseDist > 0) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const total = candlesRef.current.length;
      const ratio = touchRef.current.baseDist / dist; // pinch-out = larger dist = smaller visible
      const newVisible = Math.max(10, Math.min(total, touchRef.current.baseVisible * ratio));
      const v = viewRef.current;
      v.visibleCount = newVisible;
      drawRef.current();
    }
  }, []);

  // Attach wheel + touch via native listener with { passive: false } so preventDefault works
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    cv.addEventListener('wheel', handleWheel, { passive: false });
    cv.addEventListener('touchstart', handleTouchStart, { passive: false });
    cv.addEventListener('touchmove', handleTouchMove, { passive: false });
    return () => {
      cv.removeEventListener('wheel', handleWheel);
      cv.removeEventListener('touchstart', handleTouchStart);
      cv.removeEventListener('touchmove', handleTouchMove);
    };
  }, [handleWheel, handleTouchStart, handleTouchMove]);

  // Global mouseup so drag releases even if cursor leaves the canvas
  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseUp]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="trading-chart-canvas"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      style={{
        display:'block',
        width:'100%',
        height:'100%',
        cursor: (tool === 'cur' || !tool) ? (dragRef.current.active ? 'grabbing' : 'grab') : 'crosshair',
        userSelect:'none'
      }}
    />
  );
}

function paintDraw(ctx, d, tY, pad, W, H, sym, isDark) {
  if (!d) return;
  const bc = isDark ? '#9ab5ff' : '#3b6ef8';
  ctx.strokeStyle = bc + (d.preview ? '99' : 'ff'); ctx.lineWidth = 1.8;
  if (d.type==='lin' && d.x2!=null) {
    ctx.beginPath(); ctx.moveTo(d.x1,d.y1); ctx.lineTo(d.x2,d.y2); ctx.stroke();
    [[d.x1,d.y1],[d.x2,d.y2]].forEach(([px,py]) => { ctx.beginPath(); ctx.arc(px,py,2.5,0,Math.PI*2); ctx.fillStyle=bc; ctx.fill(); });
  } else if (d.type==='ray' && d.x2!=null) {
    const dx=d.x2-d.x1,dy=d.y2-d.y1,len=Math.sqrt(dx*dx+dy*dy)||1;
    ctx.beginPath(); ctx.moveTo(d.x1,d.y1); ctx.lineTo(d.x1+dx/len*3000,d.y1+dy/len*3000); ctx.stroke();
    ctx.beginPath(); ctx.arc(d.x1,d.y1,2.5,0,Math.PI*2); ctx.fillStyle=bc; ctx.fill();
  } else if (d.type==='hor') {
    const y = tY(d.price);
    ctx.beginPath(); ctx.setLineDash([4,3]); ctx.moveTo(pad.l,y); ctx.lineTo(W-pad.r,y); ctx.stroke(); ctx.setLineDash([]);
    ctx.fillStyle=bc; ctx.font='8px monospace'; ctx.textAlign='right';
    ctx.fillText(d.price.toFixed(sym.d), W-pad.r-2, y-2);
  } else if (d.type==='rec' && d.x2!=null) {
    const x=Math.min(d.x1,d.x2),y=Math.min(d.y1,d.y2),rw=Math.abs(d.x2-d.x1),rh=Math.abs(d.y2-d.y1);
    ctx.fillStyle=bc+'18'; ctx.fillRect(x,y,rw,rh); ctx.strokeRect(x,y,rw,rh);
  } else if (d.type==='fib' && d.p2!=null) {
    const range = d.p1 - d.p2;
    [0,.236,.382,.5,.618,.786,1].forEach((lvl,i) => {
      const fy = tY(d.p1 - range*lvl);
      const cols = ['#6b8fff','#a78bfa','#0ea56f','#f59e0b','#0ea56f','#a78bfa','#6b8fff'][i];
      ctx.beginPath(); ctx.setLineDash([3,3]); ctx.moveTo(pad.l,fy); ctx.lineTo(W-pad.r,fy);
      ctx.strokeStyle=cols+'99'; ctx.lineWidth=.8; ctx.stroke(); ctx.setLineDash([]);
      ctx.fillStyle=cols; ctx.font='8px monospace'; ctx.textAlign='right';
      ctx.fillText((lvl*100).toFixed(1)+'%', W-pad.r-2, fy-1);
    });
  }
}

/* Sub-chart: RSI, MACD, Volume */
function SubChart({ type, candles, isDark }) {
  const cvRef = useRef(null);

  function sma(d,n){return d.map((_,i)=>i<n-1?null:d.slice(i-n+1,i+1).reduce((a,b)=>a+b,0)/n);}
  function ema(d,n){const k=2/(n+1);let pv=null;return d.map((v,i)=>{if(i<n-1)return null;if(i===n-1){pv=d.slice(0,n).reduce((a,b)=>a+b,0)/n;return pv;}pv=v*k+pv*(1-k);return pv;});}
  function rsiCalc(d,n=14){const g=[],lo=[];for(let i=1;i<d.length;i++){const dx=d[i]-d[i-1];g.push(dx>0?dx:0);lo.push(dx<0?-dx:0);}const r=new Array(n).fill(null);let ag=g.slice(0,n).reduce((a,b)=>a+b,0)/n,al=lo.slice(0,n).reduce((a,b)=>a+b,0)/n;for(let i=n;i<g.length;i++){ag=(ag*(n-1)+g[i])/n;al=(al*(n-1)+lo[i])/n;r.push(al===0?100:100-100/(1+ag/al));}return r;}
  function macdCalc(d){const e12=ema(d,12),e26=ema(d,26);const mc=d.map((_,i)=>e12[i]!=null&&e26[i]!=null?e12[i]-e26[i]:null);const valid=mc.filter(v=>v!=null);const sg=ema(valid,9);return{mc,sig:[...new Array(mc.filter(v=>v===null).length).fill(null),...sg]};}

  useEffect(() => { draw(); }, [candles, isDark]);

  function draw() {
    const cv = cvRef.current; if (!cv) return;
    const area = cv.parentElement;
    const W = area.clientWidth || 440, H = area.clientHeight || 54;
    if (W < 20) return;
    const dpr = window.devicePixelRatio || 1;
    cv.width = W*dpr; cv.height = H*dpr; cv.style.width = W+'px'; cv.style.height = H+'px';
    const ctx = cv.getContext('2d'); ctx.scale(dpr, dpr);
    ctx.fillStyle = isDark ? '#111520' : '#fff'; ctx.fillRect(0,0,W,H);
    const pad = { l:4, r:62 }, cw = W-pad.l-pad.r;
    const vis = candles.slice(-50); const bw = cw/vis.length;
    const lbl = isDark ? '#6b7899' : '#9ba3c4';
    ctx.fillStyle = lbl; ctx.font = '9px sans-serif'; ctx.textAlign = 'left';

    if (type === 'rsi') {
      ctx.fillText('RSI(14)', 5, 11);
      const rv = rsiCalc(vis.map(c=>c.c));
      [30,50,70].forEach(lv => {
        const y = H - lv/100*H;
        ctx.beginPath(); ctx.moveTo(pad.l,y); ctx.lineTo(W-pad.r,y);
        ctx.strokeStyle = lv===50?(isDark?'rgba(255,255,255,.12)':'rgba(0,0,0,.1)'):(isDark?'rgba(255,255,255,.05)':'rgba(0,0,0,.05)');
        ctx.setLineDash(lv!==50?[3,3]:[]); ctx.stroke(); ctx.setLineDash([]);
        ctx.fillStyle=lbl; ctx.font='8px monospace'; ctx.textAlign='right'; ctx.fillText(lv, W-pad.r-2, y+3);
      });
      ctx.beginPath(); let f = true;
      rv.forEach((v,i) => { if(v==null)return; const x=pad.l+i*bw+bw/2,y=H-(v/100)*H; if(f){ctx.moveTo(x,y);f=false;}else ctx.lineTo(x,y); });
      const last = rv.filter(v=>v!=null).pop()||50;
      ctx.strokeStyle = last>70||last<30?(isDark?'#ff5252':'#e53e3e'):(isDark?'#6b8fff':'#3b6ef8');
      ctx.lineWidth = 1.5; ctx.stroke();
      if (last) { ctx.fillStyle=isDark?'#b4bde0':'#2d3452'; ctx.font='bold 9px monospace'; ctx.textAlign='right'; ctx.fillText(last.toFixed(1), W-pad.r-2, 12); }

    } else if (type === 'macd') {
      ctx.fillText('MACD', 5, 11);
      const { mc, sig } = macdCalc(vis.map(c=>c.c));
      const vals = [...mc,...sig].filter(v=>v!=null); if (!vals.length) return;
      const mx = Math.max(...vals.map(Math.abs)); const toM = v => H/2 - v/mx*(H/2-4);
      ctx.beginPath(); ctx.moveTo(pad.l,H/2); ctx.lineTo(W-pad.r,H/2);
      ctx.strokeStyle=isDark?'rgba(255,255,255,.1)':'rgba(0,0,0,.08)'; ctx.lineWidth=.5; ctx.stroke();
      mc.forEach((v,i) => { if(v==null)return; const x=pad.l+i*bw,y=toM(v),base=toM(0); ctx.fillStyle=v>=0?(isDark?'#10d98855':'#0ea56f55'):(isDark?'#ff525255':'#e53e3e55'); ctx.fillRect(x+1,Math.min(y,base),bw-2,Math.abs(y-base)||1); });
      ctx.beginPath(); let f = true;
      sig.forEach((v,i) => { if(v==null)return; const x=pad.l+i*bw+bw/2; if(f){ctx.moveTo(x,toM(v));f=false;}else ctx.lineTo(x,toM(v)); });
      ctx.strokeStyle='#f59e0b'; ctx.lineWidth=1.2; ctx.stroke();

    } else if (type === 'vol') {
      ctx.fillText('Объём', 5, 11);
      const mx = Math.max(...vis.map(c=>c.v||0))||1;
      vis.forEach((c,i) => { const x=pad.l+i*bw,h=(c.v/mx)*(H-14); ctx.fillStyle=c.c>=c.o?(isDark?'#10d98855':'#0ea56f55'):(isDark?'#ff525255':'#e53e3e55'); ctx.fillRect(x+1,H-h,bw-2,h); });
    }
  }

  useEffect(() => { const h=()=>draw(); window.addEventListener('resize',h); return ()=>window.removeEventListener('resize',h); }, []);

  return <canvas ref={cvRef} style={{ display:'block', width:'100%', height:'100%' }} />;
}

/* ─────────────────────────────────────────
   MAIN COMPONENT
   ───────────────────────────────────────── */
const TradingPlatform = () => {
  const authContext = useContext(AuthContext);
  const { user, logout } = authContext || {};
  // [CFD-2026-05] i18n
  const { lang, switchLang, tt } = useLanguage();

  // Trading engine from existing code (balance, history, open options)
  const {
    balance: engineBalance,
    activeTrades,
    tradeHistory,
    createTrade,
    closeTrade,
    refreshData,
  } = useTradingEngine(user) || {};

  // [CFD-2026-05] Balance comes from CRM via /api/trading/balance (db.trading_balances).
  // Fallback to 0 if no account yet — never show fake $10K to a logged-in client.
  const balance = parseFloat(engineBalance) || 0;

  // UI state
  const [isDark, setIsDark]   = useState(true);     // [CFD-2026-05] dark theme by default
  // [MODE-2026-05-28c] Persist trade mode per browser so client doesn't re-pick it every visit.
  const [mode, setMode]       = useState(() => {
    try {
      const saved = localStorage.getItem('tp_trade_mode');
      return saved === 'opt' || saved === 'cfd' ? saved : 'cfd';
    } catch (_) { return 'cfd'; }
  });
  useEffect(() => {
    try { localStorage.setItem('tp_trade_mode', mode); } catch (_) {}
  }, [mode]);
  const [curMkt, setCurMkt]   = useState('forex');
  const [sym, setSym]         = useState(MKT.forex.syms[0]);

  // Option form
  const [oDir, setODir]       = useState('c');     // 'c' call | 'p' put
  // [OPT-2026-05-13] Default $5, min $5; oAmt stored as raw string for mobile editability
  const [oAmt, setOAmt]       = useState('5');
  const [oDur, setODur]       = useState(300);

  // CFD form
  const [cDir, setCDir]       = useState('b');     // 'b' buy | 's' sell
  const [cVol, setCVol]       = useState(0.10);
  const [cSL,  setCSL]        = useState('');
  const [cTP,  setCTP]        = useState('');
  const [slMode, setSlMode]   = useState('p');     // 'p' price | 'd' dollar
  const [tpMode, setTpMode]   = useState('p');

  // [TP-2026-05-13] Tick every second so the option countdown column
  // re-renders even when no other prop changes.
  const [_tick, setTick] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(iv);
  }, []);

  // [TP-2026-05-13] Reset volume/SL/TP when the user picks a different asset.
  // Prevents accidental orders like SL=1.05 carried over from EUR/USD onto
  // BTC/USD (where prices live in the tens of thousands).
  useEffect(() => {
    setCVol(0.10);
    setCSL('');
    setCTP('');
    setSlMode('p');
    setTpMode('p');
  }, [sym?.s]);

  // CFD positions (local + API)
  const [cfdPos,   setCfdPos]   = useState([]);
  const [cfdHist,  setCfdHist]  = useState([]);
  const [pid, setPid]           = useState(1);

  // Positions table
  const [posTab, setPosTab]   = useState('o');     // 'o' | 'h'

  // Indicators
  const [inds, setInds]       = useState({ ma:false, ema:false, bb:false, rsi:false, macd:false, vol:false });

  // Drawing tools
  const [tool, setTool]       = useState('cur');
  const [draws, setDraws]     = useState([]);
  const [drawNow, setDrawNow] = useState(null);
  const canvasApiRef          = useRef(null);

  // Modals
  const [showDeposit,   setShowDeposit]   = useState(false);
  const [showWithdraw,  setShowWithdraw]  = useState(false);
  const [showMessages,  setShowMessages]  = useState(false);
  // [CFD-2026-05]
  const [showDocuments, setShowDocuments] = useState(false);
  const [showChangePw,  setShowChangePw]  = useState(false);
  const [showSupport,   setShowSupport]   = useState(false);
  const [showCalendar,  setShowCalendar]  = useState(false); // [CFD-2026-05] Economic calendar modal
  const [unreadMsg, setUnreadMsg]         = useState(0);     // [CFD-2026-05] real unread count
  const [marketDD, setMarketDD] = useState(null); // [CFD-2026-05] Open market dropdown id

  // [CFD-2026-05] Close market dropdown on outside click
  useEffect(() => {
    if (!marketDD) return;
    const h = () => setMarketDD(null);
    document.addEventListener('click', h);
    return () => document.removeEventListener('click', h);
  }, [marketDD]);
  const [supportContacts, setSupportContacts] = useState(null); // [CFD-2026-05]
  const [activeTf,      setActiveTf]      = useState('1ч'); // [CFD-2026-05] timeframe state
  const [sidebarOpen,   setSidebarOpen]   = useState(false);
  // [CFD-2026-05] Left navigation sidebar — controls main view
  const [navView, setNavView] = useState('chart'); // chart | positions | history | calendar
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [historyTypeFilter, setHistoryTypeFilter] = useState('all'); // 'all' | 'opt' | 'cfd'
  // [CFD-2026-05] Mobile responsive — track viewport width
  const [vw, setVw] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const isMobile = vw < 768;
  useEffect(() => {
    const handler = () => setVw(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [mobileMarketPicker, setMobileMarketPicker] = useState(false); // [UI-2026-05-19f] mobile assets picker overlay
  const [mobileTradePanel, setMobileTradePanel] = useState(false); // bottom-sheet trade form on mobile
  // [CFD-2026-05-24] Trade confirmation modal — shows summary before executing CFD/Option order.
  const [confirmTrade, setConfirmTrade] = useState(null);

  // [FAB-2026-05-28d] Draggable mobile trade FAB — persists position per-browser.
  const FAB_SIZE = 64;
  const fabDefaultPos = () => ({ x: 12, y: 116 });
  const [fabPos, setFabPos] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('tp_fab_pos') || 'null');
      if (saved && typeof saved.x === 'number' && typeof saved.y === 'number') return saved;
    } catch (_) {}
    return fabDefaultPos();
  });
  const fabDragRef = useRef({ dragging: false, dx: 0, dy: 0, moved: false });
  // Keep FAB within viewport on resize.
  useEffect(() => {
    const clamp = () => setFabPos(p => ({
      x: Math.max(4, Math.min(window.innerWidth  - FAB_SIZE - 4, p.x)),
      y: Math.max(60, Math.min(window.innerHeight - FAB_SIZE - 4, p.y)),
    }));
    window.addEventListener('resize', clamp);
    clamp();
    return () => window.removeEventListener('resize', clamp);
  }, []);
  const fabStart = (clientX, clientY) => {
    fabDragRef.current = { dragging: true, dx: clientX - fabPos.x, dy: clientY - fabPos.y, moved: false };
  };
  const fabMove = (clientX, clientY) => {
    if (!fabDragRef.current.dragging) return;
    const dr = fabDragRef.current;
    const nx = Math.max(4, Math.min(window.innerWidth  - FAB_SIZE - 4, clientX - dr.dx));
    const ny = Math.max(60, Math.min(window.innerHeight - FAB_SIZE - 4, clientY - dr.dy));
    if (Math.abs(clientX - (dr.dx + fabPos.x)) > 4 || Math.abs(clientY - (dr.dy + fabPos.y)) > 4) {
      dr.moved = true;
    }
    setFabPos({ x: nx, y: ny });
  };
  const fabEnd = () => {
    if (!fabDragRef.current.dragging) return;
    fabDragRef.current.dragging = false;
    try { localStorage.setItem('tp_fab_pos', JSON.stringify(fabPos)); } catch (_) {}
    // delay reset of `moved` so the click handler can read it
    setTimeout(() => { fabDragRef.current.moved = false; }, 50);
  };
  useEffect(() => {
    const onMove = (e) => {
      if (!fabDragRef.current.dragging) return;
      const t = e.touches ? e.touches[0] : e;
      fabMove(t.clientX, t.clientY);
      if (e.cancelable) e.preventDefault();
    };
    const onUp = () => fabEnd();
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [fabPos]);

  // [CFD-2026-05] SL/TP tab in right panel
  const [sltpTab, setSltpTab] = useState('sl'); // 'sl' | 'tp'

  // Branding
  // [CFD-2026-05] Use existing brand_settings instead of separate platform/branding
  // [BRAND-2026-05-18] Initialise from localStorage (populated by BrandContext on
  // every page load) so the correct logo/name renders on first paint, avoiding
  // the "PRIME → real brand" flicker.
  const [brand, setBrand] = useState(() => {
    try {
      const cachedName = localStorage.getItem('brandName') || '';
      const cachedLogo = localStorage.getItem('brandLogo') || '';
      const cachedColor = localStorage.getItem('brandColor') || '#3b6ef8';
      return {
        name: cachedName,
        logo: (cachedName || 'T').charAt(0).toUpperCase(),
        color: cachedColor,
        logo_url: cachedLogo || null,
      };
    } catch (_) {
      return { name: '', logo: 'T', color: '#3b6ef8', logo_url: null };
    }
  });

  // Notification
  const [notif, setNotif] = useState(null);

  const { prices, marketStatus, sessionCloseMsg } = useQuotes();
  // [MARKET-2026-05-15] Per-symbol session info from /api/market/hours
  const [sessionInfo, setSessionInfo] = useState({});
  // [WL-2026-05-18] Watchlist (favourite symbols)
  const [watchlist, setWatchlist] = useState([]);
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${backendUrl}/api/watchlist`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { symbols: [] })
      .then(d => setWatchlist(d?.symbols || []))
      .catch(() => {});
  }, []);
  const toggleWatchlist = async (symbolCode) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    try {
      const r = await fetch(`${backendUrl}/api/watchlist/toggle?symbol=${encodeURIComponent(symbolCode)}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) {
        const d = await r.json();
        setWatchlist(prev => d.added ? [...prev, d.symbol] : prev.filter(s => s !== d.symbol));
      }
    } catch (_) {}
  };
  useEffect(() => {
    const symList = Object.values(MKT).flatMap(m => m.syms.map(s => s.s)).join(',');
    const loadHours = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const r = await fetch(`${backendUrl}/api/market/hours?symbols=${symList}`);
        if (r.ok) setSessionInfo(await r.json());
      } catch (_) {}
    };
    loadHours();
    const iv = setInterval(loadHours, 60000); // 1 min — session boundaries don't change often
    return () => clearInterval(iv);
  }, []);
  const curPrice = prices[sym.s] || sym.p;
  const bid = parseFloat((curPrice - sym.sp/2).toFixed(sym.d));
  const ask = parseFloat((curPrice + sym.sp/2).toFixed(sym.d));

  // Load branding — refresh from server in case it changed since the cached
  // values in localStorage were stored.
  useEffect(() => {
    axios.get(`${API}/brand/settings`).then(r => {
      if (r.data) {
        const d = r.data;
        const name = d.brand_name || d.name || '';
        setBrand({
          name,
          logo: (name || 'T').charAt(0).toUpperCase(),
          color: d.primary_color || d.color || '#3b6ef8',
          logo_url: d.logo_url || (d.has_logo ? `${API}/brand/logo` : null),
        });
      }
    }).catch(() => {});
  }, []);

  // [CFD-2026-05] Poll unread message count for badge in user menu
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const fetchCount = async () => {
      try {
        const r = await fetch(`${API}/my-messages`, { headers: { Authorization: `Bearer ${token}` }});
        if (!r.ok) return setUnreadMsg(0);
        const data = await r.json();
        const arr = Array.isArray(data) ? data : [];
        const unread = arr.filter(m => !m.read && !m.is_read).length;
        setUnreadMsg(unread);
      } catch (_) { setUnreadMsg(0); }
    };
    fetchCount();
    const iv = setInterval(fetchCount, 30000); // every 30s
    return () => clearInterval(iv);
  }, [user, showMessages]);

  // Load CFD positions
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const loadPos = () =>
      axios.get(`${API}/cfd/positions`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setCfdPos(r.data || [])).catch(() => {});
    loadPos();
    // [CFD-2026-05-14] Poll backend every 5s so platform shows the SAME current_price
    // and unrealized_pnl as CRM (Yahoo Finance via backend = single source of truth).
    const iv = setInterval(loadPos, 5000);
    // [CFD-2026-05] Load CFD history too
    axios.get(`${API}/cfd/history`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setCfdHist((r.data || []).map(t => ({
        ...t, sym:t.symbol, lbl:t.symbol, dir:t.direction, vol:t.volume,
        pnl:t.realized_pnl, won:(t.realized_pnl||0)>=0,
        ca: t.closed_at ? new Date(t.closed_at).toLocaleTimeString('ru-RU',{hour:'2-digit',minute:'2-digit'}) : '—',
      })))).catch(() => {});
    return () => clearInterval(iv);
  }, [user]);

  const showMsg = useCallback((text, type='ok') => {
    setNotif({ text, type });
    setTimeout(() => setNotif(null), 4000);
  }, []);

  // Leverage for current market
  const getLev = () => {
    const mk = Object.keys(MKT).find(k => MKT[k].syms.some(s => s.s === sym.s));
    return mk ? MKT[mk].lev : 100;
  };

  // Margin calc
  // [CFD-2026-05-12] Symbol-aware: handles forex cross-pairs (GBPJPY, USDJPY)
  // + crypto + commodities + indices + stocks. See utils/financeMath.js.
  const calcMargin = () => fmCalcMargin(sym.s, cVol, curPrice, getLev());

  // CFD P&L
  // [CFD-2026-05-14] BACKEND IS THE SINGLE SOURCE OF TRUTH.
  // Use `pos.unrealized_pnl` / `pos.current_price` straight from `/api/cfd/positions`
  // (which Yahoo-Finance-refreshes per call) — guarantees platform and CRM show identical numbers.
  // Fallback to local recompute only when backend hasn't yet populated those fields.
  const getCFDPnl = (pos) => {
    if (typeof pos.unrealized_pnl === 'number') return pos.unrealized_pnl;
    const symStr = pos.symbol || pos.sym;
    const cur = pos.current_price || prices[symStr] || pos.entry_price || pos.entry;
    const d = pos.direction || pos.dir;
    const dir = (d === 'buy' || d === 'b') ? 'buy' : 'sell';
    return fmCalcPnl(symStr, dir, pos.volume || pos.vol, pos.entry_price || pos.entry, cur);
  };

  const getCFDCurrentPrice = (pos) => {
    // Prefer backend value, fall back to local feed
    return pos.current_price || prices[pos.symbol || pos.sym] || pos.entry_price || pos.entry;
  };

  const totalCFDPnl = cfdPos.reduce((s,p) => s + getCFDPnl(p), 0);
  const usedMargin  = cfdPos.reduce((s,p) => s + (p.margin||0), 0);
  // [BAL-2026-05-19f] Backend already subtracted margin from `balance` on open,
  // so the "true" wallet a trader expects to see (their deposited amount) is
  // balance + usedMargin. We expose `walletBalance` for ALL user-facing displays
  // (header, stats widget, deposit screen). Withdraw/open-position guards still
  // use raw `balance` because that's the actual free cash available to spend.
  const walletBalance = balance + usedMargin;

  // Open option — uses existing TradingEngine
  // [CFD-2026-05-23] Confirmation step before executing a trade. The actual
  // axios.post is now inside `executeOpenCFD` (called by the modal).
  const handleOpenOption = () => {
    if (marketStatus[sym.s] === 'closed') {
      return showMsg(sessionCloseMsg || 'Рынок закрыт', 'err');
    }
    const amt = parseFloat(oAmt);
    if (!isFinite(amt) || amt < 5) return showMsg('Минимальная сумма $5', 'err');
    if (amt > balance) return showMsg('Недостаточно средств', 'err');
    const lockedStrike = oDir==='c' ? ask : bid;
    setConfirmTrade({
      type: 'opt',
      sym: sym.s, label: sym.l, digits: sym.d,
      dir: oDir, amount: amt, strike: lockedStrike, duration: oDur,
    });
  };

  const executeOpenOption = async ({ symLabel, dir, amount, lockedStrike, duration }) => {
    try {
      const result = await createTrade?.(sym.s, dir==='c'?'call':'put', amount, duration, lockedStrike);
      if (result?.success !== false) {
        showMsg(`${dir==='c'?'▲ ВЫШЕ':'▼ НИЖЕ'} ${symLabel} · $${amount} · @${lockedStrike.toFixed(sym.d)}`);
        refreshData?.();
        // [PANEL-CLOSE-2026-07-17] Auto-close the mobile trade panel after
        // a successful open so the user isn't left staring at the form.
        setMobileTradePanel(false);
      } else {
        showMsg(result?.message || 'Ошибка', 'err');
      }
    } catch (e) {
      showMsg('Ошибка открытия', 'err');
    }
  };

  // [CFD-2026-05] Reload CFD positions from API
  const reloadCfdFromApi = useCallback(async () => {
    if (!user) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const r = await axios.get(`${API}/cfd/positions`, { headers: { Authorization: `Bearer ${token}` } });
      setCfdPos(r.data || []);
      const h = await axios.get(`${API}/cfd/history`, { headers: { Authorization: `Bearer ${token}` } });
      setCfdHist((h.data || []).map(t => ({
        ...t, sym:t.symbol, lbl:t.symbol, dir:t.direction, vol:t.volume,
        pnl:t.realized_pnl, won:(t.realized_pnl||0)>=0,
        ca: t.closed_at ? new Date(t.closed_at).toLocaleTimeString('ru-RU',{hour:'2-digit',minute:'2-digit'}) : '—',
      })));
    } catch (_) {}
  }, [user]);

  // [TP-2026-05-19g] Convert SL/TP from $ mode to price level.
  // Uses the symbol-aware `targetPriceForUsd` helper which is the exact
  // inverse of `calcPnl`. Previously this hard-coded `n / (vol*100000)` which
  // only matched calcPnl for *USD forex pairs (EURUSD…) — for USDJPY, cross
  // pairs, crypto, metals and indices it produced a price level that did NOT
  // actually realize the requested $ when hit (bug: $20 TP closed at +$30/$24).
  const slTpToPrice = (val, isStop, mode) => {
    if (!val) return null;
    const n = parseFloat(val);
    if (isNaN(n)) return null;
    if (mode === 'p') return n; // already a price
    const entryP = cDir==='b' ? ask : bid;
    const direction = cDir === 'b' ? 'buy' : 'sell';
    // SL targets a LOSS (negative USD), TP targets a PROFIT (positive USD).
    const usdTarget = isStop ? -Math.abs(n) : Math.abs(n);
    const px = fmTargetPriceForUsd(sym.s, direction, cVol, entryP, usdTarget);
    if (!isFinite(px) || px <= 0) return null;
    return px;
  };

  // [CFD-2026-05-23] Show confirm modal instead of executing immediately.
  const handleOpenCFD = () => {
    if (marketStatus[sym.s] === 'closed') {
      return showMsg(sessionCloseMsg || 'Рынок закрыт', 'err');
    }
    const margin = calcMargin();
    if (margin > balance) return showMsg('Недостаточно маржи', 'err');
    const entry = cDir==='b' ? ask : bid;
    setConfirmTrade({
      type: 'cfd',
      sym: sym.s, label: sym.l, digits: sym.d,
      dir: cDir, entry, vol: parseFloat(cVol)||0.01, margin,
      leverage: getLev(),
      sl: cSL ? slTpToPrice(cSL, true, slMode) : null,
      tp: cTP ? slTpToPrice(cTP, false, tpMode) : null,
      slMode, tpMode, slRaw: cSL, tpRaw: cTP,
    });
  };

  // Actual API call — invoked from the confirmation modal.
  const executeOpenCFD = async ({ entry, slPrice, tpPrice }) => {
    const margin = calcMargin();
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(`${API}/cfd/open`, {
        symbol: sym.s, direction: cDir==='b'?'buy':'sell',
        volume: parseFloat(cVol)||0.01, leverage: getLev(),
        entry_price: entry,
        stop_loss:   slPrice,
        take_profit: tpPrice,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setCfdPos(prev => [{ ...res.data, sym:res.data.symbol, dir:cDir, entry:res.data.entry_price, vol:res.data.volume }, ...prev]);
      showMsg(`${cDir==='b'?'▲ Покупка':'▼ Продажа'} ${sym.l} · залог $${margin}`);
      refreshData?.();
      reloadCfdFromApi();
      // [PANEL-CLOSE-2026-07-17] Auto-close the mobile trade panel after
      // a successful open so the user isn't left staring at the form.
      setMobileTradePanel(false);
    } catch (e) {
      if (user && token) {
        const detail = e?.response?.data?.detail || 'Не удалось открыть позицию';
        showMsg(detail, 'err');
        return;
      }
      const margin2 = calcMargin();
      const newPos = { id: pid, sym:sym.s, symbol:sym.s, lbl:sym.l, dir:cDir, direction:cDir==='b'?'buy':'sell', vol:parseFloat(cVol)||0.01, volume:parseFloat(cVol)||0.01, entry, entry_price:entry, margin:margin2, pnl:0 };
      setCfdPos(prev => [newPos, ...prev]);
      setPid(p => p+1);
      showMsg(`${cDir==='b'?'▲ Покупка':'▼ Продажа'} ${sym.l} · залог $${margin2} (демо)`);
      setMobileTradePanel(false);
    }
  };

  // Close CFD
  const handleCloseCFD = async (pos) => {
    const closePrice = prices[pos.symbol||pos.sym] || pos.entry_price||pos.entry;
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(`${API}/cfd/close/${pos.id}?current_price=${closePrice}`,{},{ headers:{ Authorization:`Bearer ${token}` }});
      const pnl = res.data.realized_pnl;
      setCfdPos(prev => prev.filter(p => p.id !== pos.id));
      setCfdHist(prev => [{ ...pos, pnl, won:pnl>=0, ca:new Date().toLocaleTimeString('ru-RU',{hour:'2-digit',minute:'2-digit'}) }, ...prev]);
      showMsg(`Закрыто: ${pnl>=0?'+':''}$${pnl.toFixed(2)}`, pnl>=0?'ok':'err');
      refreshData?.();
      reloadCfdFromApi();
    } catch (e) {
      // [CFD-2026-05] Surface API errors for authenticated users
      if (user && token) {
        const detail = e?.response?.data?.detail || 'Не удалось закрыть позицию';
        showMsg(detail, 'err');
        return;
      }
      // Local fallback (demo / no auth)
      const pnl = getCFDPnl(pos);
      setCfdPos(prev => prev.filter(p => p.id !== pos.id));
      setCfdHist(prev => [{ ...pos, pnl, won:pnl>=0, ca:new Date().toLocaleTimeString('ru-RU',{hour:'2-digit',minute:'2-digit'}) }, ...prev]);
      showMsg(`Закрыто: ${pnl>=0?'+':''}$${pnl.toFixed(2)} (демо)`, pnl>=0?'ok':'err');
    }
  };

  // SL/TP step
  const stepSL = dir => {
    const st = parseFloat((curPrice * 0.003).toFixed(sym.d));
    if (slMode==='p') { const cur = parseFloat(cSL)||curPrice; setCSL((cur+dir*st).toFixed(sym.d)); }
    else setCSL(v => Math.max(1,(parseFloat(v)||10)+dir*5).toFixed(0));
  };
  const stepTP = dir => {
    const st = parseFloat((curPrice * 0.003).toFixed(sym.d));
    if (tpMode==='p') { const cur = parseFloat(cTP)||curPrice; setCTP((cur+dir*st).toFixed(sym.d)); }
    else setCTP(v => Math.max(1,(parseFloat(v)||10)+dir*5).toFixed(0));
  };

  // Canvas events for drawing
  const handleCanvasReady = useCallback(({ fromY, toY, canvasRef }) => {
    const cv = canvasRef.current;
    if (!cv) return;

    cv.onmousemove = e => {
      const r = cv.getBoundingClientRect();
      if (drawNow) setDrawNow(d => d ? {...d, x2:e.clientX-r.left, y2:e.clientY-r.top} : d);
    };

    cv.onmousedown = e => {
      if (tool==='cur') return;
      const r = cv.getBoundingClientRect();
      const x = e.clientX-r.left, y = e.clientY-r.top;
      const price = fromY(y);
      if (tool==='hor') { setDraws(d => [...d, {type:'hor', price}]); return; }
      if (tool==='fib') {
        setDrawNow(d => {
          if (!d) return { type:'fib', p1:price, x1:x, y1:y };
          setDraws(dd => [...dd, {...d, p2:price}]);
          return null;
        });
        return;
      }
      setDrawNow({ type:tool, x1:x, y1:y, x2:x, y2:y, price });
    };

    cv.onmouseup = e => {
      setDrawNow(d => {
        if (!d || (tool!=='lin'&&tool!=='rec'&&tool!=='ray')) return d;
        const r = cv.getBoundingClientRect();
        setDraws(dd => [...dd, {...d, x2:e.clientX-r.left, y2:e.clientY-r.top}]);
        return null;
      });
    };
  }, [tool, drawNow]);

  // ── STYLES (inline for portability) ──
  const C = {
    bg:'#f0f3fa', s:'#fff', s2:'#f5f7fd', s3:'#eceffe',
    b1:'#dce2f5', b2:'#c0cae8',
    t1:'#0d1117', t2:'#303a5a', t3:'#6b7494', t4:'#9ba3c4',
    G:'#0ea56f', Gbg:'#e6f7f1', Gbd:'#0ea56f33', Gt:'#064d33',
    R:'#e53e3e', Rbg:'#fef0f0', Rbd:'#e53e3e33', Rt:'#7a1515',
    B:'#3b6ef8', Bbg:'#eef2ff', Bbd:'#3b6ef833', Bt:'#1a3a8f',
    Y:'#d97706', Ybg:'#fffbeb', Ybd:'#f59e0b33', Yt:'#78450f',
  };
  if (isDark) Object.assign(C, {
    // [CFD-2026-05] Deep navy/purple palette matching new design
    bg:'#0a0d1f', s:'#10142b', s2:'#161b35', s3:'#1f2540',
    b1:'#252d48', b2:'#333f62',
    t1:'#eef0fb', t2:'#b4bde0', t3:'#7d86a8', t4:'#3a4468',
    G:'#10d988', Gbg:'#0a2218', Gbd:'#10d98855', Gt:'#5fffc0',
    R:'#ff5252', Rbg:'#220a0a', Rbd:'#ff525255', Rt:'#ffaaaa',
    B:'#7c5cf6', Bbg:'#1a1840', Bbd:'#7c5cf655', Bt:'#cfc4ff',
    Y:'#fbbf24', Ybg:'#1a1200', Ybd:'#fbbf2433', Yt:'#fde68a',
  });

  // [TP-2026-05-19] User-requested polish (video 2026-05-18):
  // Form inputs/select slightly larger so they're tappable on mobile.
  const fi = { width:'100%', fontSize:15, padding:'12px 14px', borderRadius:8, border:`1.5px solid ${C.b2}`, background:isDark?'#0d1020':C.bg, color:C.t1, fontWeight:600, outline:'none', fontVariantNumeric:'tabular-nums' };
  const fsel = { ...fi, cursor:'pointer' };
  const gobtn = (bg) => ({ width:'100%', padding:'14px', borderRadius:10, border:'none', fontSize:14, fontWeight:700, cursor:'pointer', color:'#fff', background:bg, letterSpacing:.3, transition:'all .15s' });

  // Candles state for sub-charts
  const [candles, setCandles] = useState([]);
  useEffect(() => {
    let p = curPrice * 0.997;
    const c = [];
    for (let i = 0; i < 60; i++) {
      const o=p,mv=(Math.random()-.48)*p*.0018,cc=o+mv;
      c.push({o,h:Math.max(o,cc)+Math.random()*p*.001,l:Math.min(o,cc)-Math.random()*p*.001,c:cc,v:Math.round(1000+Math.random()*9000)});
      p=cc;
    }
    setCandles(c);
  }, [sym.s]);

  // Sync last candle
  useEffect(() => {
    if (!candles.length) return;
    setCandles(prev => {
      const next = [...prev];
      const last = {...next[next.length-1], c:curPrice, h:Math.max(next[next.length-1].h,curPrice), l:Math.min(next[next.length-1].l,curPrice)};
      next[next.length-1] = last;
      return next;
    });
  }, [curPrice]);

  const dirBadge = (d) => {
    const up = d==='c'||d==='call'||d==='b'||d==='buy';
    const label = d==='c'?'▲ ВЫШЕ':d==='p'?'▼ НИЖЕ':d==='call'?'▲ ВЫШЕ':d==='put'?'▼ НИЖЕ':d==='b'||d==='buy'?'▲ BUY':'▼ SELL';
    return <span style={{background:up?C.Gbg:C.Rbg,color:up?C.Gt:C.Rt,padding:'2px 7px',borderRadius:4,fontSize:10,fontWeight:700,border:`1px solid ${up?C.Gbd:C.Rbd}`}}>{label}</span>;
  };
  const statBadge = (won) => (
    <span style={{background:won?C.Gbg:C.Rbg,color:won?C.Gt:C.Rt,padding:'2px 6px',borderRadius:4,fontSize:10,fontWeight:700,border:`1px solid ${won?C.Gbd:C.Rbd}`}}>
      {won?'Прибыль':'Убыток'}
    </span>
  );

  // [CHART-2026-05-19d] Binary options no longer draw P/L lines on the chart
  // (user-explicit: "в опционах эти функции не нужны"). CFD positions are
  // injected directly into <LightweightCandleChart openPositions={...}/>.

  return (
    <div style={{background:C.bg,color:C.t1,fontFamily:'-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',fontSize:12,minHeight:'100vh',display:'flex',flexDirection:'column'}}>

      {/* ── TOP NAV ── [CFD-2026-05] redesigned + mobile responsive */}
      <div style={{padding:isMobile?'8px 10px':'10px 14px',display:'flex',alignItems:'center',gap:isMobile?6:10,flexShrink:0,background:C.bg,flexWrap:isMobile?'nowrap':'nowrap'}}>
        {/* Mobile: hamburger to open left nav drawer */}
        {isMobile && (
          <button onClick={()=>setMobileSidebarOpen(true)} data-testid="mobile-menu-btn"
            style={{width:36,height:36,display:'flex',alignItems:'center',justifyContent:'center',borderRadius:9,border:`1px solid ${C.b1}`,background:C.s,color:C.t1,cursor:'pointer',flexShrink:0}}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="5" x2="15" y2="5"/><line x1="3" y1="9" x2="15" y2="9"/><line x1="3" y1="13" x2="15" y2="13"/></svg>
          </button>
        )}
        {/* Brand */}
        <div style={{display:'flex',alignItems:'center',gap:9,padding:isMobile?'5px 10px':'6px 12px',background:C.s,borderRadius:10,border:`1px solid ${C.b1}`,flexShrink:0}}>
          {brand.logo_url ? (
            <img src={brand.logo_url} alt="logo" style={{height:isMobile?18:22,maxWidth:80,objectFit:'contain',borderRadius:4}} onError={(e)=>{e.target.style.display='none';}} />
          ) : brand.name ? (
            <div style={{width:isMobile?22:26,height:isMobile?22:26,background:'linear-gradient(135deg, #f6c742, #d4a017)',borderRadius:6,display:'flex',alignItems:'center',justifyContent:'center',fontSize:isMobile?11:13,fontWeight:800,color:'#1a1a2e'}}>{brand.logo}</div>
          ) : null}
          {!isMobile && brand.name && <span style={{fontSize:14,fontWeight:700,color:C.t1,letterSpacing:.5}}>{brand.name}</span>}
        </div>
        {/* [UI-2026-05-19f] Mobile market picker button — sits next to the logo so
            users can switch instrument category without opening the full drawer. */}
        {isMobile && (
          <button onClick={()=>setMobileMarketPicker(true)} data-testid="mobile-market-picker-btn"
            style={{display:'inline-flex',alignItems:'center',gap:6,padding:'6px 11px',borderRadius:10,
              border:`1px solid ${C.B}`,background:`linear-gradient(135deg, ${C.B}, ${isDark?'#5b3fde':'#3b6ef8'})`,
              color:'#fff',cursor:'pointer',fontSize:12,fontWeight:700,flexShrink:0,
              boxShadow:`0 2px 8px ${C.Bbd}`,touchAction:'manipulation'}}>
            <span style={{fontSize:14,lineHeight:1}}>≡</span>
            <span style={{whiteSpace:'nowrap'}}>{curMkt==='fav'?tt('Избранное'):tt(MKT[curMkt]?.label||'Рынки')}</span>
            <ChevronDown size={12} strokeWidth={2.5}/>
          </button>
        )}
        {/* Market category dropdowns — hidden on mobile (in drawer instead) */}
        {!isMobile && (
        <div style={{display:'flex',alignItems:'center',gap:6,position:'relative'}}>
          {/* [WL-2026-05-18] Favourites pill — first slot for quick access */}
          <button
            onClick={()=>{ setCurMkt('fav'); setMarketDD(null); }}
            data-testid="market-fav"
            title={tt('Избранное')}
            style={{display:'inline-flex',alignItems:'center',gap:5,padding:'8px 14px',borderRadius:9,fontSize:13,fontWeight:700,
              border:`1px solid ${curMkt==='fav'?C.B:'transparent'}`,
              background: curMkt==='fav' ? 'linear-gradient(135deg, #f6c742, #d4a017)' : C.s,
              color: curMkt==='fav' ? '#1a1a2e' : C.t1, cursor:'pointer', transition:'all .15s' }}>
            <span style={{fontSize:15,lineHeight:1}}>★</span>
            {tt('Избранное')}
            {watchlist.length > 0 && (
              <span style={{fontSize:10,padding:'1px 6px',borderRadius:8,background:curMkt==='fav'?'rgba(0,0,0,.15)':C.Bbg,color:curMkt==='fav'?'#1a1a2e':C.Bt,fontWeight:700}}>{watchlist.length}</span>
            )}
          </button>
          {Object.keys(MKT).map(k => (
            <div key={k} style={{position:'relative'}}>
              <button
                onClick={(e)=>{ e.stopPropagation(); setMarketDD(marketDD===k?null:k); }}
                data-testid={`market-${k}`}
                style={{display:'inline-flex',alignItems:'center',gap:4,padding:'8px 14px',borderRadius:9,fontSize:13,fontWeight:600,
                  border:`1px solid ${curMkt===k?C.B:'transparent'}`,
                  background: curMkt===k ? 'linear-gradient(135deg, #6d4af0, #5b3fde)' : C.s,
                  color: curMkt===k ? '#fff' : C.t1, cursor:'pointer', transition:'all .15s' }}>
                {tt(MKT[k].label)}
                <ChevronDown size={14} strokeWidth={2.5} style={{opacity:.7, transform:marketDD===k?'rotate(180deg)':'none', transition:'transform .15s'}} />
              </button>
              {marketDD===k && (
                <div onClick={(e)=>e.stopPropagation()}
                  data-testid={`market-dropdown-${k}`}
                  style={{position:'absolute',top:'calc(100% + 6px)',left:0,background:C.s,border:`1px solid ${C.b1}`,borderRadius:12,minWidth:260,padding:6,zIndex:200,boxShadow:'0 12px 40px rgba(0,0,0,.45)',maxHeight:380,overflowY:'auto'}}>
                  <div style={{padding:'6px 10px 8px',fontSize:11,color:C.t3,fontWeight:700,letterSpacing:.4,textTransform:'uppercase'}}>{tt(MKT[k].label)}</div>
                  {MKT[k].syms.map(s => {
                    const p = prices[s.s]||s.p, chg=((p-BASE_PRICES[s.s])/BASE_PRICES[s.s]*100).toFixed(2), up=parseFloat(chg)>=0;
                    const active = s.s===sym.s && k===curMkt;
                    return (
                      <button key={s.s}
                        onClick={()=>{ setCurMkt(k); setSym(s); setDraws([]); setDrawNow(null); setMarketDD(null); }}
                        data-testid={`dropdown-asset-${s.s}`}
                        style={{display:'flex',alignItems:'center',gap:10,padding:'8px 10px',borderRadius:8,cursor:'pointer',width:'100%',border:'none',
                          background: active ? (isDark?'rgba(124,92,246,.18)':'rgba(124,92,246,.10)') : 'transparent',
                          textAlign:'left', transition:'background .12s'}}
                        onMouseEnter={(e)=>{if(!active)e.currentTarget.style.background=C.s2;}}
                        onMouseLeave={(e)=>{if(!active)e.currentTarget.style.background='transparent';}}>
                        <FlagIcon symbol={s.s} size={24} />
                        <div style={{display:'flex',flexDirection:'column',flex:1,gap:2}}>
                          <span style={{fontSize:12,fontWeight:700,color:C.t1}}>{s.l}</span>
                          <span style={{fontSize:10,color:C.t3}}>{s.s}</span>
                        </div>
                        <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:2}}>
                          <span style={{fontSize:12,fontWeight:600,color:C.t2,fontVariantNumeric:'tabular-nums'}}>{p.toFixed(s.d)}</span>
                          <span style={{fontSize:10,fontWeight:700,color:up?C.G:C.R,fontVariantNumeric:'tabular-nums'}}>{up?'+':''}{chg}%</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
        )}
        {/* Right cluster — bell/theme/lang moved to user sidebar drawer */}
        <div style={{marginLeft:'auto',display:'flex',alignItems:'center',gap:isMobile?6:8}}>
          <button onClick={()=>setSidebarOpen(true)} data-testid="user-menu-btn"
            style={{display:'flex',alignItems:'center',gap:isMobile?7:11,padding:isMobile?'5px 10px 5px 5px':'7px 16px 7px 6px',background:C.s,border:`1px solid ${C.b1}`,borderRadius:12,cursor:'pointer',flexShrink:0,transition:'all .15s'}}>
            <div style={{width:isMobile?30:38,height:isMobile?30:38,borderRadius:'50%',background:'linear-gradient(135deg, #7c5cf6, #5b3fde)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:isMobile?11:13,fontWeight:800,color:'#fff'}}>
              {(user?.name||'JD').split(' ').map(w=>w[0]).join('').substring(0,2).toUpperCase()}
            </div>
            {!isMobile && (
              <div style={{textAlign:'left',lineHeight:1.25}}>
                <div style={{fontSize:14,fontWeight:700,color:C.t1}}>{user?.name||'John Doe'}</div>
                <span style={{fontSize:13,color:C.G,fontWeight:700,fontVariantNumeric:'tabular-nums'}}>${walletBalance.toFixed(2)}</span>
              </div>
            )}
            {isMobile && <span style={{fontSize:12,color:C.G,fontWeight:700,fontVariantNumeric:'tabular-nums'}}>${walletBalance.toFixed(0)}</span>}
            <ChevronDown size={isMobile?13:15} color={C.t3} />
          </button>
        </div>
      </div>

      {/* ── ASSET CARDS — large with flags / smaller on mobile ── */}
      <div style={{padding:isMobile?'4px 10px 8px':'4px 14px 10px',flexShrink:0}}>
        <div style={{display:'flex',alignItems:'stretch',gap:6,background:C.s,borderRadius:12,border:`1px solid ${C.b1}`,padding:'6px'}}>
          <button onClick={()=>document.getElementById('sins').scrollBy({left:-200,behavior:'smooth'})} style={{width:isMobile?22:30,display:'flex',alignItems:'center',justifyContent:'center',border:'none',background:'transparent',color:C.t2,cursor:'pointer',borderRadius:6,flexShrink:0}}>
            <ChevronLeft size={isMobile?16:20}/>
          </button>
          <div id="sins" className="hide-scrollbar" style={{display:'flex',gap:6,overflowX:'auto',flex:1,WebkitOverflowScrolling:'touch'}}>
            {(curMkt === 'fav'
              ? Object.values(MKT).flatMap(m => m.syms).filter(s => watchlist.includes(s.s))
              : MKT[curMkt].syms
            ).map(s => {
              const p = prices[s.s]||s.p, chg=((p-BASE_PRICES[s.s])/BASE_PRICES[s.s]*100).toFixed(2), up=parseFloat(chg)>=0;
              const active = s.s===sym.s;
              const closed = marketStatus[s.s] === 'closed';
              const fav = watchlist.includes(s.s);
              return (
                <div key={s.s} data-testid={`asset-card-${s.s}`}
                  style={{display:'flex',alignItems:'center',gap:isMobile?7:10,padding:isMobile?'7px 10px':'10px 14px',borderRadius:10,
                    border:`1.5px solid ${active?C.B:'transparent'}`,
                    background: active ? (isDark?'#1a1840':'#eef0ff') : C.s2,
                    color:C.t1, cursor:'pointer', whiteSpace:'nowrap', flexShrink:0,
                    minWidth: isMobile?110:140, transition:'all .15s',
                    boxShadow: active ? `0 0 0 3px ${C.B}22` : 'none',
                    opacity: closed ? .55 : 1,
                    position:'relative'}}
                  onClick={()=>{setSym(s);setDraws([]);setDrawNow(null);}}>
                  <FlagIcon symbol={s.s} size={isMobile?22:28} />
                  <div style={{display:'flex',flexDirection:'column',alignItems:'flex-start',lineHeight:1.2,flex:1}}>
                    <div style={{fontSize:isMobile?11:13,fontWeight:700,color:C.t1,display:'flex',alignItems:'center',gap:5}}>
                      {s.l}
                      {closed && <span title={tt('Рынок закрыт')} style={{fontSize:9,padding:'1px 5px',borderRadius:4,background:C.Ybg,color:C.Yt,border:`1px solid ${C.Ybd}`,fontWeight:700}}>⏸</span>}
                    </div>
                    <div style={{display:'flex',alignItems:'center',gap:7,marginTop:3}}>
                      <span style={{fontSize:isMobile?11:13,fontWeight:600,color:C.t2,fontVariantNumeric:'tabular-nums'}}>{p.toFixed(s.d)}</span>
                      <span style={{fontSize:isMobile?9:11,fontWeight:700,color:up?C.G:C.R,fontVariantNumeric:'tabular-nums'}}>{up?'+':''}{chg}%</span>
                    </div>
                  </div>
                  {/* [WL-2026-05-18] Favourite star toggle */}
                  <span onClick={(e)=>{e.stopPropagation(); toggleWatchlist(s.s);}}
                    data-testid={`fav-toggle-${s.s}`}
                    style={{cursor:'pointer',fontSize:14,padding:'2px 4px',color:fav?'#fbbf24':C.t3,
                      transition:'color .15s, transform .15s', userSelect:'none'}}>
                    {fav ? '★' : '☆'}
                  </span>
                </div>
              );
            })}
            {curMkt === 'fav' && watchlist.length === 0 && (
              <div style={{padding:'10px 14px',color:C.t3,fontSize:12,fontStyle:'italic'}} data-testid="watchlist-empty">
                {tt('Нет избранных. Нажмите ☆ на любой инструмент.')}
              </div>
            )}
          </div>
          <button onClick={()=>document.getElementById('sins').scrollBy({left:200,behavior:'smooth'})} style={{width:isMobile?22:30,display:'flex',alignItems:'center',justifyContent:'center',border:'none',background:'transparent',color:C.t2,cursor:'pointer',borderRadius:6,flexShrink:0}}>
            <ChevronRight size={isMobile?16:20}/>
          </button>
        </div>
      </div>

      {/* ── INDICATORS ROW ── */}
      <div style={{height:28,background:C.s2,borderBottom:`1px solid ${C.b1}`,display:'flex',alignItems:'center',padding:'0 8px',gap:4,flexShrink:0}}>
        <span style={{fontSize:11,color:C.t2,marginRight:4,fontWeight:700}}>Инд:</span>
        {['ma','ema','bb','rsi','macd','vol'].map(k => (
          <button key={k} onClick={()=>setInds(prev=>({...prev,[k]:!prev[k]}))} data-testid={`ind-btn-${k}`} style={{padding:'3px 10px',fontSize:11,fontWeight:700,borderRadius:4,height:22,border:inds[k]?`1px solid ${C.B}`:`1px solid ${isDark?'#2d3559':'#d6dcef'}`,background:inds[k]?C.Bbg:(isDark?'#1a2138':'#fff'),color:inds[k]?C.Bt:C.t1,cursor:'pointer',transition:'all .12s',letterSpacing:.3}}>
            {k==='vol'?'Объём':k.toUpperCase()}
          </button>
        ))}
      </div>

      {/* ── MAIN: left sidebar | tools | chart | right panel ── */}
      <div style={{display:'flex',flex:1,overflow:'hidden'}}>

        {/* [CFD-2026-05] LEFT NAVIGATION SIDEBAR — slide drawer on mobile */}
        <div data-testid="left-nav"
          style={{
            width:isMobile ? (mobileSidebarOpen?220:0) : (navCollapsed?54:88),
            background:C.s,
            borderRight:`1px solid ${C.b1}`,
            display:'flex',flexDirection:'column',alignItems:'stretch',
            padding:isMobile?(mobileSidebarOpen?'10px 6px':0):'10px 6px',
            gap:6,flexShrink:0,
            transition:'width .15s, padding .15s',
            position:isMobile?'fixed':'relative',
            top:isMobile?0:'auto', left:0, height:isMobile?'100vh':'auto',
            zIndex:isMobile?1500:'auto',
            overflow: isMobile && !mobileSidebarOpen ? 'hidden' : 'visible',
            boxShadow: isMobile && mobileSidebarOpen ? '0 0 30px rgba(0,0,0,.5)' : 'none',
          }}>
          {isMobile && mobileSidebarOpen && (
            <button onClick={()=>setMobileSidebarOpen(false)} data-testid="mobile-nav-close"
              style={{alignSelf:'flex-end',padding:6,border:'none',background:'transparent',color:C.t2,cursor:'pointer'}}><span style={{fontSize:22,lineHeight:1}}>×</span></button>
          )}
          {/* [CFD-2026-05] Mobile-only: market selector inside the drawer */}
          {isMobile && mobileSidebarOpen && (
            <div style={{padding:'4px 4px 8px',borderBottom:`1px solid ${C.b1}`,marginBottom:6}}>
              <div style={{fontSize:9,color:C.t3,fontWeight:700,letterSpacing:.4,textTransform:'uppercase',marginBottom:6,paddingLeft:6}}>{tt('Рынки')}</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:5}}>
                {/* [WL-2026-05-18] Favourites first */}
                <button onClick={()=>{ setCurMkt('fav'); setDraws([]); setDrawNow(null); setMobileSidebarOpen(false); }} data-testid="mobile-market-fav"
                  style={{padding:'9px 8px',fontSize:12,fontWeight:700,borderRadius:8,
                    border:`1px solid ${curMkt==='fav'?C.B:'transparent'}`,
                    background: curMkt==='fav' ? 'linear-gradient(135deg, #f6c742, #d4a017)' : C.s2,
                    color: curMkt==='fav' ? '#1a1a2e' : C.t1, cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:4}}>
                  <span style={{fontSize:13}}>★</span>{tt('Избранное')}{watchlist.length>0 && ` (${watchlist.length})`}
                </button>
                {Object.keys(MKT).map(k => (
                  <button key={k} onClick={()=>{ setCurMkt(k); setSym(MKT[k].syms[0]); setDraws([]); setDrawNow(null); setMobileSidebarOpen(false); }} data-testid={`mobile-market-${k}`}
                    style={{padding:'9px 8px',fontSize:12,fontWeight:600,borderRadius:8,
                      border:`1px solid ${curMkt===k?C.B:'transparent'}`,
                      background: curMkt===k ? 'linear-gradient(135deg, #6d4af0, #5b3fde)' : C.s2,
                      color: curMkt===k ? '#fff' : C.t1, cursor:'pointer'}}>
                    {tt(MKT[k].label)}
                  </button>
                ))}
              </div>
            </div>
          )}
          {[
            { id:'chart',     icon:<LineChart size={18}/>,     label:tt('График') },
            { id:'positions', icon:<ClipboardList size={18}/>, label:tt('Открытые позиции') },
            { id:'history',   icon:<ClockIcon size={18}/>,     label:tt('История') },
            { id:'calendar',  icon:<CalendarDays size={18}/>,  label:tt('Эк. календарь') },
          ].map(item => {
            const active = navView === item.id;
            return (
              <button key={item.id} onClick={() => {
                if (item.id === 'calendar') {
                  setShowCalendar(true);
                } else {
                  setNavView(item.id);
                }
                if (isMobile) setMobileSidebarOpen(false);
              }}
                data-testid={`nav-${item.id}`}
                style={{display:'flex',flexDirection:isMobile?'row':'column',alignItems:'center',gap:isMobile?10:5,padding:isMobile?'12px 14px':'10px 4px',borderRadius:10,border:'none',
                  background: active ? (isDark?'rgba(124,92,246,.18)':'rgba(124,92,246,.10)') : 'transparent',
                  color: active ? C.B : C.t2, cursor:'pointer', transition:'all .12s',justifyContent:isMobile?'flex-start':'center'}}>
                {item.icon}
                {(!navCollapsed || isMobile) && <span style={{fontSize:isMobile?13:10,fontWeight:600,textAlign:isMobile?'left':'center',lineHeight:1.2}}>{item.label}</span>}
              </button>
            );
          })}
          <div style={{flex:1}}/>
          <button onClick={() => setSidebarOpen(true)} data-testid="nav-settings"
            style={{display:'flex',flexDirection:'column',alignItems:'center',gap:5,padding:'10px 4px',borderRadius:10,border:'none',background:'transparent',color:C.t2,cursor:'pointer'}}>
            <SettingsIcon size={18}/>
            {!navCollapsed && <span style={{fontSize:10,fontWeight:600}}>{tt('Настройки')}</span>}
          </button>
          <button onClick={() => setNavCollapsed(!navCollapsed)} data-testid="nav-collapse"
            style={{display:'flex',alignItems:'center',justifyContent:'center',gap:4,padding:'7px',borderRadius:8,border:`1px solid ${C.b1}`,background:'transparent',color:C.t3,cursor:'pointer',fontSize:10,fontWeight:600}}>
            {navCollapsed ? <ChevronRight size={14}/> : <><ChevronLeft size={14}/> {tt('Свернуть')}</>}
          </button>
        </div>

        {/* LEFT TOOL STRIP — hidden on mobile */}
        {!isMobile && (
        <div style={{width:30,background:C.s,borderRight:`1px solid ${C.b1}`,display:'flex',flexDirection:'column',alignItems:'center',padding:'4px 0',gap:1,flexShrink:0}}>
          {[
            { id:'cur', title:'Курсор', svg:<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M3 2l8 5-4.5 1L8 12 6 13 4.5 9 1 10.5z"/></svg> },
          ].map(t => <ToolBtn key={t.id} t={t} active={tool===t.id} onClick={()=>setTool(t.id)} C={C} />)}
          <div style={{width:18,height:1,background:C.b1,margin:'2px 0'}}/>
          {[
            { id:'lin', title:'Линия тренда', svg:<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="2" y1="12" x2="12" y2="2"/><circle cx="2" cy="12" r="1.2" fill="currentColor" stroke="none"/><circle cx="12" cy="2" r="1.2" fill="currentColor" stroke="none"/></svg> },
            { id:'hor', title:'Горизонталь', svg:<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="1" y1="7" x2="13" y2="7"/><line x1="1" y1="4.5" x2="1" y2="9.5" strokeDasharray="1.5 1"/><line x1="13" y1="4.5" x2="13" y2="9.5" strokeDasharray="1.5 1"/></svg> },
            { id:'ray', title:'Луч', svg:<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="2" y1="12" x2="12" y2="2"/><circle cx="2" cy="12" r="1.2" fill="currentColor" stroke="none"/><path d="M9 2.5l2.5-.5-.5 2.5" strokeWidth="1.2"/></svg> },
            { id:'rec', title:'Прямоугольник', svg:<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="3" width="10" height="8" rx="1"/></svg> },
          ].map(t => <ToolBtn key={t.id} t={t} active={tool===t.id} onClick={()=>setTool(t.id)} C={C} />)}
          <div style={{width:18,height:1,background:C.b1,margin:'2px 0'}}/>
          {[
            { id:'fib', title:'Фибоначчи', svg:<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2"><line x1="1" y1="12" x2="13" y2="12"/><line x1="1" y1="8.5" x2="13" y2="8.5"/><line x1="1" y1="6" x2="13" y2="6"/><line x1="1" y1="4" x2="13" y2="4"/><line x1="1" y1="2" x2="13" y2="2"/></svg> },
          ].map(t => <ToolBtn key={t.id} t={t} active={tool===t.id} onClick={()=>setTool(t.id)} C={C} />)}
          <div style={{width:18,height:1,background:C.b1,margin:'2px 0'}}/>
          <button onClick={()=>{setDraws([]);setDrawNow(null);}} title="Очистить рисунки" style={{width:24,height:24,display:'flex',alignItems:'center',justifyContent:'center',borderRadius:4,border:'1px solid transparent',background:'transparent',color:C.Rt,cursor:'pointer',transition:'all .12s',padding:0}} data-testid="clear-drawings-btn">
            {/* [CFD-2026-05] trash bin icon */}
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 4h10"/>
              <path d="M5 4V2.5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1V4"/>
              <path d="M3 4l.7 7.6a1 1 0 0 0 1 .9h4.6a1 1 0 0 0 1-.9L11 4"/>
              <line x1="6" y1="7" x2="6" y2="11"/>
              <line x1="8" y1="7" x2="8" y2="11"/>
            </svg>
          </button>
        </div>
        )}

        {/* CHART COLUMN */}
        <div style={{display:'flex',flexDirection:'column',flex:1,minWidth:0}}>
          {/* [CFD-2026-05] Chart header bar — symbol name top-left + actions top-right */}
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:isMobile?'6px 10px':'8px 14px',background:C.s,borderBottom:`1px solid ${C.b1}`,flexShrink:0,gap:8,flexWrap:isMobile?'wrap':'nowrap'}}>
            <div style={{display:'flex',alignItems:'center',gap:isMobile?8:14,flexWrap:'wrap'}} data-testid="chart-header-info">
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <FlagIcon symbol={sym.s} size={isMobile?20:22} />
                <span style={{fontSize:isMobile?13:14,fontWeight:700,color:C.t1}}>{sym.l}</span>
                <span style={{fontSize:11,color:C.t3,fontWeight:500}}>· {activeTf}</span>
                <span style={{display:'inline-block',width:8,height:8,borderRadius:'50%',background:C.G,boxShadow:`0 0 6px ${C.G}88`,marginLeft:4}}/>
              </div>
              {!isMobile && (() => {
                const cs = candles.length ? candles[candles.length-1] : null;
                if (!cs) return null;
                const items = [
                  ['О', cs.o],
                  ['В', cs.h],
                  ['Н', cs.l],
                  ['З', cs.c],
                ];
                return (
                  <div style={{display:'flex',alignItems:'center',gap:10,fontSize:11,fontVariantNumeric:'tabular-nums'}}>
                    {items.map(([k,v]) => (
                      <span key={k} style={{display:'flex',gap:4}}>
                        <span style={{color:C.t3,fontWeight:600}}>{k}</span>
                        <span style={{color:cs.c>=cs.o?C.G:C.R,fontWeight:600}}>{v.toFixed(sym.d)}</span>
                      </span>
                    ))}
                  </div>
                );
              })()}
            </div>
            <div style={{display:'flex',alignItems:'center',gap:6,flexShrink:0}}>
              <div style={{display:'flex',alignItems:'center',background:C.s2,borderRadius:8,border:`1px solid ${C.b1}`,padding:2,gap:1}}>
                {['1м','5м','15м','1ч','4ч','1д','1н'].map(tf => (
                  <button key={tf} onClick={()=>setActiveTf(tf)} data-testid={`tf-btn-${tf}`}
                    style={{padding:isMobile?'4px 7px':'5px 10px',fontSize:isMobile?10:11,fontWeight:tf===activeTf?700:600,borderRadius:6,border:'none',
                      background:tf===activeTf?C.B:'transparent',
                      color:tf===activeTf?'#fff':C.t2,cursor:'pointer',transition:'all .12s'}}>
                    {tf}
                  </button>
                ))}
              </div>
              {!isMobile && (
                <>
                  {/* [CFD-2026-05] Camera + Fullscreen removed per user request */}
                </>
              )}
            </div>
          </div>

          {/* [CFD-2026-05] Mobile drawing tools — horizontal strip below chart header */}
          {isMobile && (
            <div style={{display:'flex',alignItems:'center',gap:4,padding:'6px 10px',background:C.s,borderBottom:`1px solid ${C.b1}`,overflowX:'auto',flexShrink:0}} className="hide-scrollbar">
              {[
                { id:'cur', title:'Курсор', svg:<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M3 2l8 5-4.5 1L8 12 6 13 4.5 9 1 10.5z"/></svg> },
                { id:'lin', title:'Линия', svg:<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="2" y1="12" x2="12" y2="2"/><circle cx="2" cy="12" r="1.2" fill="currentColor" stroke="none"/><circle cx="12" cy="2" r="1.2" fill="currentColor" stroke="none"/></svg> },
                { id:'hor', title:'Горизонталь', svg:<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="1" y1="7" x2="13" y2="7"/><line x1="1" y1="4.5" x2="1" y2="9.5" strokeDasharray="1.5 1"/><line x1="13" y1="4.5" x2="13" y2="9.5" strokeDasharray="1.5 1"/></svg> },
                { id:'ray', title:'Луч', svg:<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="2" y1="12" x2="12" y2="2"/><circle cx="2" cy="12" r="1.2" fill="currentColor" stroke="none"/><path d="M9 2.5l2.5-.5-.5 2.5" strokeWidth="1.2"/></svg> },
                { id:'rec', title:'Прямоугольник', svg:<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="3" width="10" height="8" rx="1"/></svg> },
                { id:'fib', title:'Фибоначчи', svg:<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2"><line x1="1" y1="12" x2="13" y2="12"/><line x1="1" y1="8.5" x2="13" y2="8.5"/><line x1="1" y1="6" x2="13" y2="6"/></svg> },
              ].map(t => (
                <button key={t.id} onClick={()=>setTool(t.id)} title={t.title} data-testid={`mobile-tool-${t.id}`}
                  style={{flexShrink:0,width:36,height:32,display:'flex',alignItems:'center',justifyContent:'center',borderRadius:7,border:`1px solid ${tool===t.id?C.B:C.b1}`,background:tool===t.id?C.Bbg:C.s2,color:tool===t.id?C.B:C.t1,cursor:'pointer'}}>
                  {t.svg}
                </button>
              ))}
              <div style={{width:1,height:20,background:C.b1,margin:'0 2px'}}/>
              <button onClick={()=>{setDraws([]);setDrawNow(null);}} title="Очистить" data-testid="mobile-clear-drawings"
                style={{flexShrink:0,width:36,height:32,display:'flex',alignItems:'center',justifyContent:'center',borderRadius:7,border:`1px solid ${C.b1}`,background:C.s2,color:C.Rt,cursor:'pointer'}}>
                <Trash2 size={14}/>
              </button>
              {/* Indicators trigger - opens panel */}
              {['ma','ema','bb','rsi'].map(k => (
                <button key={k} onClick={()=>setInds(prev=>({...prev,[k]:!prev[k]}))} data-testid={`mobile-ind-${k}`}
                  style={{flexShrink:0,padding:'5px 8px',fontSize:10,fontWeight:700,borderRadius:7,border:`1px solid ${inds[k]?C.B:C.b1}`,background:inds[k]?C.Bbg:C.s2,color:inds[k]?C.B:C.t2,cursor:'pointer'}}>
                  {k.toUpperCase()}
                </button>
              ))}
            </div>
          )}
          {/* [CFD-2026-05] Chart area OR alternative views based on left-nav selection */}
          <div id="chart-area" style={{background:navView==='chart'?C.s:C.bg,position:'relative',overflow:navView==='chart'?'hidden':'auto',flex:1,minHeight:200}}>
            {navView === 'chart' && (
              // [TV-2026-05-18] TradingView Lightweight Charts is now the only chart.
              <LightweightCandleChart
                sym={sym} prices={prices} mode={mode} isDark={isDark} tf={activeTf}
                openPositions={cfdPos.filter(p => (p.symbol||p.sym)===sym.s).map(p => ({
                  sym: p.symbol||p.sym, dir: p.direction||p.dir, entry: p.entry_price||p.entry,
                  sl: p.sl||p.stop_loss, tp: p.tp||p.take_profit, kind: 'cfd', id: p.id,
                }))}
                indicators={inds}
                marketClosed={marketStatus[sym.s] === 'closed'}
                marketClosedMessage={sessionInfo[sym.s]?.message_ru || sessionCloseMsg || ''}
              />
            )}
            {navView === 'positions' && (
              <div style={{padding:'18px 20px'}} data-testid="view-positions">
                <h2 style={{margin:'0 0 16px 0',fontSize:18,fontWeight:700,color:C.t1,display:'flex',alignItems:'center',gap:10}}>
                  <ClipboardList size={20} color={C.B}/>
                  {tt('Открытые позиции')}
                  <span style={{padding:'3px 10px',borderRadius:12,background:C.Bbg,color:C.Bt,fontSize:12,fontWeight:700}}>
                    {(activeTrades||[]).length + cfdPos.length}
                  </span>
                </h2>
                {(activeTrades||[]).length === 0 && cfdPos.length === 0 ? (
                  <div style={{textAlign:'center',padding:60,color:C.t3,fontSize:14}}>
                    <ClipboardList size={48} style={{opacity:.3,marginBottom:14}}/>
                    <div>{tt('Нет открытых позиций')}</div>
                  </div>
                ) : (
                  <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'repeat(auto-fill, minmax(360px, 1fr))',gap:12}}>
                    {[
                      ...(activeTrades||[]).map(t => ({ kind:'opt', ...t })),
                      ...cfdPos.map(p => ({ kind:'cfd', ...p })),
                    ].map(p => {
                      const isOpt = p.kind === 'opt';
                      const dir = isOpt ? p.direction : (p.direction || p.dir);
                      const isBuy = dir === 'buy' || dir === 'call' || dir === 'b' || dir === 'c';
                      // [TP-2026-05-14] Accept camelCase from TradingEngine
                      const entry = isOpt ? (p.strike_price ?? p.strikePrice) : (p.entry_price || p.entry);
                      // [CFD-2026-05-14] CFD uses backend `current_price` (single source of truth, syncs with CRM)
                      const cur = isOpt
                        ? (prices[p.asset] || entry)
                        : (p.current_price || prices[p.symbol || p.sym] || entry);
                      const pnl = isOpt ? (p.expected_payout || (p.amount * 1.85) || 0) : getCFDPnl(p);
                      const sl = p.sl || p.stop_loss;
                      const tp = p.tp || p.take_profit;
                      const optExpiry = isOpt ? (p.expires_at || p.expiresAt) : null;
                      const rem = optExpiry ? Math.max(0, Math.ceil((new Date(optExpiry) - Date.now()) / 1000)) : 0;
                      const fmtRem = (s) => {
                        if (s <= 0) return tt('Обработка...');
                        const h = Math.floor(s/3600), m = Math.floor((s%3600)/60), sec = s%60;
                        return h>0 ? `${h}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}` : `${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
                      };
                      return (
                        <div key={`${p.kind}-${p.id}`} data-testid={`pos-card-${p.id}`}
                          style={{background:C.s,border:`1px solid ${C.b1}`,borderRadius:12,padding:14,display:'flex',flexDirection:'column',gap:10}}>
                          <div style={{display:'flex',alignItems:'center',gap:10}}>
                            <FlagIcon symbol={isOpt ? p.asset : (p.symbol||p.sym)} size={26}/>
                            <div style={{flex:1}}>
                              <div style={{fontSize:14,fontWeight:700,color:C.t1}}>{isOpt ? p.asset : (p.symbol||p.sym)}</div>
                              <div style={{fontSize:11,color:isOpt?C.G:C.B,fontWeight:600}}>{isOpt ? tt('Опцион') : 'CFD'} · {dirBadge(isOpt?(dir==='call'?'c':'p'):(isBuy?'b':'s'))}</div>
                            </div>
                            {!isOpt && (
                              <button onClick={()=>handleCloseCFD(p)} data-testid={`close-pos-${p.id}`}
                                style={{padding:'6px 12px',fontSize:11,fontWeight:700,borderRadius:7,border:`1px solid ${C.Rbd}`,background:C.Rbg,color:C.Rt,cursor:'pointer'}}>
                                {tt('Закрыть')}
                              </button>
                            )}
                            {isOpt && (
                              <span data-testid={`pos-card-countdown-${p.id}`}
                                style={{padding:'3px 9px',fontSize:10,fontWeight:700,borderRadius:6,background:rem>0?C.Ybg:C.s3,color:rem>0?C.Yt:C.t3,border:`1px solid ${rem>0?C.Ybd:C.b2}`,fontVariantNumeric:'tabular-nums'}}>
                                {fmtRem(rem)}
                              </span>
                            )}
                          </div>
                          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,fontSize:11}}>
                            <div>
                              <div style={{color:C.t3,fontWeight:600,fontSize:10}}>{tt('Цена входа')}</div>
                              <div style={{color:C.t1,fontWeight:700,fontVariantNumeric:'tabular-nums'}}>{parseFloat(entry||0).toFixed(5)}</div>
                            </div>
                            <div>
                              <div style={{color:C.t3,fontWeight:600,fontSize:10}}>{isOpt?tt('Сумма'):tt('Текущая цена')}</div>
                              <div style={{color:C.t1,fontWeight:700,fontVariantNumeric:'tabular-nums'}}>{isOpt ? '$'+parseFloat(p.amount||0).toFixed(2) : parseFloat(cur||0).toFixed(5)}</div>
                            </div>
                            <div>
                              <div style={{color:C.t3,fontWeight:600,fontSize:10}}>{isOpt?tt('Выплата'):'P/L'}</div>
                              <div style={{color:isOpt||pnl>=0?C.G:C.R,fontWeight:700,fontVariantNumeric:'tabular-nums'}}>{isOpt ? '$'+parseFloat(pnl).toFixed(2) : (pnl>=0?'+':'')+'$'+pnl.toFixed(2)}</div>
                            </div>
                            <div>
                              <div style={{color:C.t3,fontWeight:600,fontSize:10}}>{isOpt?tt('Объём'):tt('Объём')+'/'+tt('Маржа')}</div>
                              <div style={{color:C.t1,fontWeight:700,fontVariantNumeric:'tabular-nums'}}>{isOpt ? '—' : (parseFloat(p.volume||p.vol||0).toFixed(2)+' / $'+Math.round(p.margin||0))}</div>
                            </div>
                          </div>
                          {!isOpt && (sl || tp) && (
                            <div style={{display:'flex',gap:10,fontSize:10,color:C.t3,paddingTop:6,borderTop:`1px solid ${C.b1}`}}>
                              {sl && <span>SL: <strong style={{color:C.Rt}}>{parseFloat(sl).toFixed(4)}</strong></span>}
                              {tp && <span>TP: <strong style={{color:C.Gt}}>{parseFloat(tp).toFixed(4)}</strong></span>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {navView === 'history' && (
              <div style={{padding:'18px 20px'}} data-testid="view-history">
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14,flexWrap:'wrap',gap:10}}>
                  <h2 style={{margin:0,fontSize:18,fontWeight:700,color:C.t1,display:'flex',alignItems:'center',gap:10}}>
                    <ClockIcon size={20} color={C.B}/>
                    {tt('История')}
                  </h2>
                  {/* [CFD-2026-05] Type filter pills — separate Опционы / CFD / Все */}
                  <div style={{display:'flex',gap:4,background:C.s,border:`1px solid ${C.b1}`,borderRadius:9,padding:3}} data-testid="history-type-filter">
                    {[
                      ['all', tt('Все'), (tradeHistory||[]).length + cfdHist.length],
                      ['opt', tt('Опционы'), (tradeHistory||[]).length],
                      ['cfd', 'CFD', cfdHist.length],
                    ].map(([id,lbl,cnt]) => (
                      <button key={id} onClick={()=>setHistoryTypeFilter(id)} data-testid={`hist-filter-${id}`}
                        style={{padding:'6px 12px',fontSize:11,fontWeight:700,borderRadius:7,border:'none',cursor:'pointer',
                          background: historyTypeFilter===id ? 'linear-gradient(135deg, #6d4af0, #5b3fde)' : 'transparent',
                          color: historyTypeFilter===id ? '#fff' : C.t2, display:'flex',alignItems:'center',gap:6}}>
                        {lbl} {cnt>0 && <span style={{padding:'1px 6px',borderRadius:8,background:historyTypeFilter===id?'rgba(255,255,255,.25)':C.s2,fontSize:10}}>{cnt}</span>}
                      </button>
                    ))}
                  </div>
                </div>
                {(() => {
                  const items = [
                    ...(historyTypeFilter==='cfd' ? [] : (tradeHistory||[]).map(h => ({ ...h, _kind:'opt', _ts:h.completed_at?new Date(h.completed_at).getTime():0 }))),
                    ...(historyTypeFilter==='opt' ? [] : cfdHist.map(h => ({ ...h, _kind:'cfd', _ts:h.closed_at?new Date(h.closed_at).getTime():(h.ca?Date.parse(h.ca):0) }))),
                  ].sort((a,b) => (b._ts||0) - (a._ts||0));
                  if (items.length === 0) {
                    return (
                      <div style={{textAlign:'center',padding:60,color:C.t3,fontSize:14}}>
                        <ClockIcon size={48} style={{opacity:.3,marginBottom:14}}/>
                        <div>{tt('История пуста')}</div>
                      </div>
                    );
                  }
                  return (
                    <div style={{background:C.s,borderRadius:12,border:`1px solid ${C.b1}`,overflow:'hidden'}}>
                      <div style={{overflowX:'auto'}}>
                        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,minWidth:600}}>
                          <thead>
                            <tr style={{background:C.s2}}>
                              {[tt('Актив'),tt('Тип'),tt('Направление'),tt('Цена входа'),tt('Сумма'),tt('Результат'),tt('Время')].map(h => (
                                <th key={h} style={{padding:'10px 12px',textAlign:'left',fontSize:10,color:C.t3,fontWeight:700,letterSpacing:.3,textTransform:'uppercase',borderBottom:`1px solid ${C.b1}`,whiteSpace:'nowrap'}}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((h,i) => {
                              const isOpt = h._kind === 'opt';
                              const won = isOpt ? (h.status==='won') : ((h.realized_pnl ?? h.pnl ?? 0) >= 0);
                              const d = isOpt ? (h.direction==='call'?'c':'p') : (h.dir||h.direction);
                              const amount = isOpt ? parseFloat(h.amount||0) : parseFloat(h.margin||h.volume*100||0);
                              const entry = isOpt ? (h.strike_price ?? h.entry_price) : (h.entry_price ?? h.entry);
                              const res = isOpt
                                ? (won ? '+$'+parseFloat(h.profit_loss||0).toFixed(2) : '-$'+parseFloat(h.amount||0).toFixed(2))
                                : `${(h.realized_pnl ?? h.pnl ?? 0)>=0?'+':''}$${Math.abs(h.realized_pnl ?? h.pnl ?? 0).toFixed(2)}`;
                              const time = isOpt
                                ? (h.completed_at?new Date(h.completed_at).toLocaleString('ru-RU',{day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'}):'—')
                                : (h.closed_at?new Date(h.closed_at).toLocaleString('ru-RU',{day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'}):(h.ca||'—'));
                              return (
                                <tr key={`${h._kind}-${i}`} style={{borderBottom:`1px solid ${C.s3}`}}>
                                  <td style={{padding:'10px 12px'}}><div style={{display:'flex',alignItems:'center',gap:8}}><FlagIcon symbol={h.asset||h.symbol||h.lbl} size={20}/><span style={{fontWeight:700,color:C.t1}}>{h.asset||h.symbol||h.lbl}</span></div></td>
                                  <td style={{padding:'10px 12px'}}><span style={{padding:'3px 9px',borderRadius:6,fontSize:10,fontWeight:700,background:isOpt?C.Gbg:C.Bbg,color:isOpt?C.Gt:C.Bt,border:`1px solid ${isOpt?C.Gbd:C.Bbd}`}}>{isOpt?tt('Опцион'):'CFD'}</span></td>
                                  <td style={{padding:'10px 12px'}}>{dirBadge(d)}</td>
                                  <td style={{padding:'10px 12px',fontVariantNumeric:'tabular-nums',color:C.t1,fontWeight:600}}>{entry?parseFloat(entry).toFixed(5):'—'}</td>
                                  <td style={{padding:'10px 12px',fontVariantNumeric:'tabular-nums'}}>${amount.toFixed(2)}</td>
                                  <td style={{padding:'10px 12px',fontWeight:700,color:won?C.G:C.R,fontVariantNumeric:'tabular-nums'}}>{res}</td>
                                  <td style={{padding:'10px 12px',color:C.t3,fontSize:11,fontVariantNumeric:'tabular-nums'}}>{time}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
          {inds.rsi  && <div style={{height:54,background:C.s,borderTop:`1px solid ${C.b1}`}}><SubChart type="rsi"  candles={candles} isDark={isDark}/></div>}
          {inds.macd && <div style={{height:54,background:C.s,borderTop:`1px solid ${C.b1}`}}><SubChart type="macd" candles={candles} isDark={isDark}/></div>}
          {inds.vol  && <div style={{height:40,background:C.s,borderTop:`1px solid ${C.b1}`}}><SubChart type="vol"  candles={candles} isDark={isDark}/></div>}
        </div>
        {/* [CFD-2026-05] Stats bar AND positions tabs moved OUT of chart column to be always visible */}

        {/* RIGHT PANEL — bottom sheet on mobile, wider on desktop */}
        <div data-testid="right-panel"
          style={{
            width: isMobile ? (mobileTradePanel?'100%':0) : 320,
            background:C.s, borderLeft:isMobile?'none':`1px solid ${C.b1}`,
            display:'flex',flexDirection:'column',flexShrink:0,
            position:isMobile?'fixed':'relative',
            bottom:isMobile?0:'auto',right:isMobile?0:'auto',left:isMobile?0:'auto',
            top:isMobile?'auto':'auto',
            height:isMobile?(mobileTradePanel?'80vh':0):'auto',
            zIndex:isMobile?1400:'auto',
            borderTopLeftRadius:isMobile?20:0,
            borderTopRightRadius:isMobile?20:0,
            overflow:'hidden',
            transition:'height .25s',
            boxShadow:isMobile&&mobileTradePanel?'0 -10px 40px rgba(0,0,0,.5)':'none',
          }}>
          {isMobile && mobileTradePanel && (
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 14px',borderBottom:`1px solid ${C.b1}`,flexShrink:0}}>
              <div style={{width:36,height:4,borderRadius:2,background:C.b2,margin:'0 auto'}}/>
              <button onClick={()=>setMobileTradePanel(false)} data-testid="mobile-trade-close" style={{position:'absolute',right:12,top:8,background:'transparent',border:'none',color:C.t2,fontSize:24,cursor:'pointer',padding:4}}>×</button>
            </div>
          )}
          <div style={{padding:'18px 16px',overflowY:'auto',flex:1}}>
            {notif && (
              <div style={{padding:'7px 10px',borderRadius:7,fontSize:11,marginBottom:8,fontWeight:600,borderLeft:'3px solid',background:notif.type==='ok'?C.Gbg:C.Rbg,color:notif.type==='ok'?C.Gt:C.Rt,borderLeftColor:notif.type==='ok'?C.G:C.R}}>
                {notif.text}
              </div>
            )}
            <div style={{fontSize:11,fontWeight:700,padding:'5px 12px',borderRadius:8,textAlign:'center',marginBottom:10,letterSpacing:.4,background:mode==='opt'?C.Gbg:C.Bbg,color:mode==='opt'?C.Gt:C.Bt,border:`1px solid ${mode==='opt'?C.Gbd:C.Bbd}`}}>
              {mode==='opt'?'ОПЦИОНЫ':'CFD'}
            </div>

            {/* [MARKET-2026-05-15] Market-closed banner for the active symbol */}
            {marketStatus[sym.s] === 'closed' && (
              <div data-testid="market-closed-banner"
                style={{padding:'10px 12px',borderRadius:8,marginBottom:10,fontSize:11,fontWeight:600,
                  background:C.Ybg, color:C.Yt, border:`1.5px solid ${C.Ybd}`,
                  display:'flex',alignItems:'center',gap:8}}>
                <span style={{fontSize:16}}>⏸</span>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontWeight:700,marginBottom:2}}>{tt('Рынок закрыт')} · {sym.l}</div>
                  <div style={{fontSize:10,fontWeight:500,opacity:.85}}>
                    {sessionInfo[sym.s]?.message_ru || sessionCloseMsg || tt('Котировки заморожены. Сделки временно недоступны.')}
                  </div>
                  {sessionInfo[sym.s]?.next_open && (
                    <div style={{fontSize:10,fontWeight:600,opacity:.95,marginTop:3,fontVariantNumeric:'tabular-nums'}}>
                      {tt('Открытие')}: {new Date(sessionInfo[sym.s].next_open).toLocaleString(undefined, {weekday:'short', month:'short', day:'numeric', hour:'2-digit', minute:'2-digit'})}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* OPTIONS FORM */}
            {mode==='opt' && <>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:4,marginBottom:9}}>
                {[['c','▲','ВЫШЕ'],[' p','▼','НИЖЕ']].map(([d,icon,label]) => (
                  <div key={d.trim()} onClick={()=>setODir(d.trim())} style={{padding:'8px 5px',borderRadius:7,cursor:'pointer',textAlign:'center',fontWeight:700,fontSize:11,border:`2px solid ${oDir===d.trim()?d.trim()==='c'?C.G:C.R:C.b2}`,background:oDir===d.trim()?d.trim()==='c'?C.Gbg:C.Rbg:C.s2,color:oDir===d.trim()?d.trim()==='c'?C.Gt:C.Rt:C.t3,transition:'all .15s'}}>
                    <div style={{fontSize:17,lineHeight:1.2,marginBottom:2}}>{icon}</div>{label}
                  </div>
                ))}
              </div>
              <div style={{marginBottom:8}}>
                <div style={{fontSize:10,color:C.t2,marginBottom:3,fontWeight:600}}>Сумма ($) · мин. $5</div>
                <div style={{position:'relative'}}>
                  <span style={{position:'absolute',left:11,top:'50%',transform:'translateY(-50%)',color:C.t3,fontSize:14,fontWeight:700,pointerEvents:'none'}}>$</span>
                  <input type="number" inputMode="decimal" value={oAmt} min={5} step={1}
                    onChange={e=>setOAmt(e.target.value)}
                    onBlur={()=>{ const n = parseFloat(oAmt); if (!isFinite(n) || n < 5) setOAmt('5'); }}
                    data-testid="opt-amount-input"
                    style={{...fi,paddingLeft:24}}/>
                </div>
              </div>
              <div style={{marginBottom:8}}>
                <div style={{fontSize:10,color:C.t2,marginBottom:3,fontWeight:600}}>Экспирация</div>
                <select value={oDur} onChange={e=>setODur(parseInt(e.target.value))} style={fsel} data-testid="opt-duration-select">
                  <option value={300}>5 минут</option>
                  <option value={900}>15 минут</option>
                  <option value={3600}>1 час</option>
                  <option value={14400}>4 часа</option>
                  <option value={86400}>24 часа</option>
                  <option value={604800}>1 неделя</option>
                  <option value={2592000}>30 дней</option>
                </select>
              </div>
              <div style={{background:C.s2,border:`1.5px solid ${C.b2}`,borderRadius:8,padding:'8px 10px',marginBottom:9}}>
                {(() => { const amtN = parseFloat(oAmt) || 0; return [{k:'Страйк',v:curPrice.toFixed(sym.d)},{k:'Инвестиция',v:'$'+amtN.toFixed(2)},{k:'Прибыль 85%',v:'+$'+(amtN*.85).toFixed(2),g:true},{k:'При прибыли',v:'$'+(amtN*1.85).toFixed(2),g:true,big:true}].map((r,i)=>(
                  <div key={i} style={{display:'flex',justifyContent:'space-between',fontSize:11,padding:'2px 0',...(i===2?{borderTop:`1px solid ${C.b1}`,marginTop:3,paddingTop:3}:{})}}>
                    <span style={{color:C.t3,fontWeight:500}}>{r.k}</span>
                    <span style={{fontWeight:700,fontVariantNumeric:'tabular-nums',color:r.g?C.G:C.t1,fontSize:r.big?12:11}}>{r.v}</span>
                  </div>
                )); })()}
              </div>
              <button onClick={handleOpenOption}
                disabled={marketStatus[sym.s]==='closed'}
                data-testid="opt-submit-btn"
                style={{...gobtn(oDir==='c'?C.G:C.R), ...(marketStatus[sym.s]==='closed'?{opacity:.4,cursor:'not-allowed',filter:'grayscale(.8)'}:{})}}>
                {marketStatus[sym.s]==='closed' ? '⏸ '+tt('Рынок закрыт') : (oDir==='c'?'▲ Открыть ВЫШЕ':'▼ Открыть НИЖЕ')}
              </button>
            </>}

            {/* CFD FORM */}
            {mode==='cfd' && <>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:5,marginBottom:10}}>
                {[['s','babid','▼ SELL',tt('Продажа')],['b','baask','▲ BUY',tt('Покупка')]].map(([d,cls,dir,lbl])=>(
                  <div key={d} onClick={()=>setCDir(d)} style={{padding:'8px 5px',borderRadius:7,textAlign:'center',cursor:'pointer',border:`2px solid ${d==='s'?C.R:C.G}`,background:d==='s'?C.Rbg:C.Gbg,boxShadow:cDir===d?`0 0 0 2px ${C.b2}`:'none',transition:'all .15s'}}>
                    <div style={{fontSize:9,fontWeight:600,marginBottom:1,color:d==='s'?C.Rt:C.Gt}}>{lbl}</div>
                    <div style={{fontSize:10,fontWeight:700,marginBottom:2,color:d==='s'?C.Rt:C.Gt}}>{dir}</div>
                    <div style={{fontSize:13,fontWeight:700,fontVariantNumeric:'tabular-nums',color:d==='s'?C.Rt:C.Gt}}>{d==='s'?bid.toFixed(sym.d):ask.toFixed(sym.d)}</div>
                  </div>
                ))}
              </div>
              <div style={{marginBottom:8}}>
                <div style={{fontSize:10,color:C.t2,marginBottom:3,fontWeight:600}}>{tt('Объём (лоты)')}</div>
                {/* [UX-2026-05-19e] Mobile-friendly stepper: large +/− buttons (≥44px tap target)
                    with violet brand color so they're easy to spot on small screens. Also fixes
                    the bug where clearing the input auto-reverted to 0.01 — value can be empty
                    during typing; on blur we sanitize to min 0.01. */}
                <div style={{display:'flex',alignItems:'stretch',gap:6}}>
                  <button type="button" data-testid="cfd-volume-dec"
                    onClick={()=>setCVol(prev=>{const n=Math.max(0.01,parseFloat((parseFloat(prev)||0.01)-0.01).toFixed(2));return n;})}
                    style={{flex:'0 0 48px',height:48,borderRadius:10,border:'none',background:C.B,color:'#fff',fontSize:26,fontWeight:800,cursor:'pointer',lineHeight:1,boxShadow:`0 2px 8px ${C.Bbd}`,touchAction:'manipulation',userSelect:'none'}}>−</button>
                  <input type="number" inputMode="decimal" value={cVol}
                    onChange={e=>{const v=e.target.value; setCVol(v===''?'':parseFloat(v));}}
                    onBlur={()=>{const n=parseFloat(cVol); if(!isFinite(n)||n<0.01) setCVol(0.01);}}
                    data-testid="cfd-volume-input"
                    style={{...fi,flex:1,textAlign:'center',fontSize:18,fontWeight:700,padding:'12px 6px'}}/>
                  <button type="button" data-testid="cfd-volume-inc"
                    onClick={()=>setCVol(prev=>{const n=parseFloat(((parseFloat(prev)||0)+0.01).toFixed(2));return n;})}
                    style={{flex:'0 0 48px',height:48,borderRadius:10,border:'none',background:C.B,color:'#fff',fontSize:26,fontWeight:800,cursor:'pointer',lineHeight:1,boxShadow:`0 2px 8px ${C.Bbd}`,touchAction:'manipulation',userSelect:'none'}}>+</button>
                </div>
              </div>
              <div style={{fontSize:10,color:C.t3,marginBottom:8,padding:'4px 8px',background:C.s2,borderRadius:5,border:`1px solid ${C.b1}`}}>
                {tt('Плечо')}: <strong style={{color:C.t1}}>1:{getLev()}</strong>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:5,marginBottom:8}}>
                {[{label:'Stop Loss',val:cSL,set:setCSL,m:slMode,setM:setSlMode,stepD:()=>stepSL(-1),stepU:()=>stepSL(1),hintId:'sl'},{label:'Take Profit',val:cTP,set:setCTP,m:tpMode,setM:setTpMode,stepD:()=>stepTP(-1),stepU:()=>stepTP(1),hintId:'tp'}].map(f=>(
                  <div key={f.label}>
                    <div style={{fontSize:10,color:C.t2,fontWeight:600,display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:3}}>
                      {f.label}
                      {/* [UX-2026-05-19e] Mode toggle (Цена / $) larger — previous 1×5px padding
                          + 9px font produced a ~12px tap target which is impossible on phones.
                          Now ~36×28 each (≥WCAG 2.5.5 enhanced size guidance). */}
                      <div style={{display:'flex',border:`1.5px solid ${C.b2}`,borderRadius:6,overflow:'hidden'}}>
                        {['p','d'].map(m=><button key={m} type="button" onClick={()=>f.setM(m)}
                          data-testid={`cfd-${f.hintId}-mode-${m==='p'?'price':'usd'}`}
                          style={{padding:'5px 12px',minWidth:38,minHeight:28,fontSize:12,fontWeight:700,border:'none',background:f.m===m?C.B:'transparent',color:f.m===m?'#fff':C.t2,cursor:'pointer',lineHeight:1.1,touchAction:'manipulation'}}>{m==='p'?'Цена':'$'}</button>)}
                      </div>
                    </div>
                    <div style={{display:'flex',border:`1.5px solid ${C.b2}`,borderRadius:6,overflow:'hidden',background:isDark?'#0d1020':C.bg}}>
                      <button onClick={f.stepD} style={{background:C.s3,border:'none',color:C.t1,fontSize:16,padding:'0 6px',cursor:'pointer',height:30}}>−</button>
                      <input type="number" value={f.val} placeholder="—" onChange={e=>f.set(e.target.value)} style={{flex:1,width:0,fontSize:11,padding:'4px 2px',border:'none',background:'transparent',color:C.t1,textAlign:'center',fontVariantNumeric:'tabular-nums',fontWeight:600,outline:'none'}}/>
                      <button onClick={f.stepU} style={{background:C.s3,border:'none',color:C.t1,fontSize:16,padding:'0 6px',cursor:'pointer',height:30}}>+</button>
                    </div>
                    <div style={{fontSize:9,marginTop:1,textAlign:'center',fontVariantNumeric:'tabular-nums',fontWeight:500,color:f.val?(f.hintId==='sl'?C.R:C.G):C.t4}}>
                      {f.val ? (f.m==='p' ? `${parseFloat(f.val).toFixed(sym.d)} · ${(Math.abs(parseFloat(f.val)-curPrice)/curPrice*100).toFixed(2)}%` : `${f.hintId==='sl'?'Риск':'Цель'} $${parseFloat(f.val).toFixed(2)}`) : (f.hintId==='sl'?'− ниже рынка':'+ выше рынка')}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{background:C.s2,border:`1.5px solid ${C.b2}`,borderRadius:8,padding:'8px 10px',marginBottom:9}}>
                {[{k:tt('Цена входа'),v:(cDir==='b'?ask:bid).toFixed(sym.d)},{k:tt('Объём'),v:(parseFloat(cVol)||0).toFixed(2)+' '+tt('лот')},{k:tt('Позиция'),v:'$'+Math.round(calcMargin()*getLev()).toLocaleString()},{k:tt('Залог (маржа)'),v:'~$'+calcMargin().toLocaleString(),y:true,big:true}].map((r,i)=>(
                  <div key={i} style={{display:'flex',justifyContent:'space-between',fontSize:11,padding:'2px 0',...(i===3?{borderTop:`1px solid ${C.b1}`,marginTop:3,paddingTop:3}:{})}}>
                    <span style={{color:C.t3,fontWeight:r.big?700:500}}>{r.k}</span>
                    <span style={{fontWeight:700,fontVariantNumeric:'tabular-nums',color:r.y?C.Y:C.t1,fontSize:r.big?12:11}}>{r.v}</span>
                  </div>
                ))}
              </div>
              <button onClick={handleOpenCFD}
                disabled={marketStatus[sym.s]==='closed'}
                data-testid="cfd-submit-btn"
                style={{...gobtn(cDir==='b'?C.G:C.R), ...(marketStatus[sym.s]==='closed'?{opacity:.4,cursor:'not-allowed',filter:'grayscale(.8)'}:{})}}>
                {marketStatus[sym.s]==='closed' ? '⏸ '+tt('Рынок закрыт') : (cDir==='b'?'▲ '+tt('Открыть ПОКУПКУ'):'▼ '+tt('Открыть ПРОДАЖУ'))}
              </button>
            </>}
          </div>
        </div>
      </div>

      {/* ── POSITIONS TABLE [CFD-2026-05] enhanced design ── */}
      <div style={{borderTop:`1px solid ${C.b1}`,background:C.bg,flexShrink:0}}>
        {/* [CFD-2026-05] Stats CARDS with lucide icons — 2x2 grid on mobile */}
        {/* [BAL-2026-05-19f] Корректные формулы по стандарту MT4/MT5.
            Backend хранит `balance` УЖЕ за вычетом маржи (см. cfd_open: $inc balance:-margin),
            поэтому:
              wallet (то, что юзер считает "балансом") = balance + usedMargin
              equity = wallet + floatingPnL = balance + usedMargin + totalCFDPnl
              freeMargin = equity - usedMargin = balance + totalCFDPnl
              marginLevel% = equity / usedMargin × 100
            Прежняя формула freeMargin = balance - usedMargin + pnl вычитала маржу ВТОРОЙ
            раз (она уже была вычтена из balance), из-за чего "Свободно" было занижено
            ровно на сумму маржи. Эта правка совпадает с поведением MT4/MT5. */}
        {(() => {
          const wallet = balance + usedMargin;
          const equity = wallet + totalCFDPnl;
          const freeMargin = balance + totalCFDPnl;
          const marginLevel = usedMargin > 0 ? (equity / usedMargin * 100) : Infinity;
          const isMarginCall = usedMargin > 0 && marginLevel < 120; // <20% free margin
          return (
            <>
              {isMarginCall && (
                <div data-testid="margin-call-warning"
                  style={{margin:isMobile?'8px 10px 0':'10px 14px 0',padding:'10px 14px',borderRadius:10,background:'rgba(239,68,68,.12)',border:'1px solid rgba(239,68,68,.5)',display:'flex',alignItems:'center',gap:10,color:C.R,fontSize:13,fontWeight:600}}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 1L1 19h18L10 1z"/><line x1="10" y1="8" x2="10" y2="13"/><circle cx="10" cy="16" r="1" fill="currentColor"/></svg>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:700,marginBottom:2}}>{tt('Margin Level низкий')} — {marginLevel.toFixed(0)}%</div>
                    <div style={{fontSize:11,opacity:.85,fontWeight:500}}>{tt('Риск принудительного закрытия. Закройте часть позиций или пополните счёт.')}</div>
                  </div>
                </div>
              )}
              {/* [UI-2026-05-19f] Compact stats: shorter cards & smaller icons free
                  ~15-20px of vertical space for the chart. */}
              <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr 1fr':'repeat(4, 1fr)',gap:isMobile?5:6,padding:isMobile?'4px 10px':'5px 14px'}}>
                {[
                  { label:tt('Баланс'), val:`$${wallet.toLocaleString('ru-RU',{minimumFractionDigits:2,maximumFractionDigits:2})}`, color:C.t1, Icon:Wallet, iconBg:'#3b82f6' },
                  { label:tt('Маржа'),   val:`$${Math.round(usedMargin).toLocaleString()}`, color:C.Y, Icon:Lock, iconBg:'#f59e0b' },
                  { label:tt('П/У'), val:`${totalCFDPnl>=0?'+':''}$${totalCFDPnl.toFixed(2)}`, color:totalCFDPnl>=0?C.G:C.R, Icon:TrendingUp, iconBg: totalCFDPnl>=0?'#10b981':'#ef4444' },
                  { label:tt('Свободно'), val:`$${Math.round(freeMargin).toLocaleString()}`, color:isMarginCall?C.R:C.t1, Icon:PieChart, iconBg:isMarginCall?'#ef4444':'#7c5cf6', alert:isMarginCall },
                ].map((s,i) => (
                  <div key={i} data-testid={`stats-${s.label.toLowerCase()}`}
                    style={{display:'flex',alignItems:'center',gap:isMobile?6:8,padding:isMobile?'5px 8px':'6px 10px',background:C.s,borderRadius:8,
                      border:`1px solid ${s.alert?'rgba(239,68,68,.6)':C.b1}`,
                      boxShadow:s.alert?'0 0 0 1px rgba(239,68,68,.3)':'none'}}>
                    <div style={{width:isMobile?22:26,height:isMobile?22:26,borderRadius:isMobile?6:7,background:`${s.iconBg}22`,display:'flex',alignItems:'center',justifyContent:'center',color:s.iconBg,flexShrink:0}}>
                      <s.Icon size={isMobile?12:14} strokeWidth={2.4}/>
                    </div>
                    <div style={{display:'flex',flexDirection:'column',gap:0,minWidth:0,flex:1}}>
                      <span style={{fontSize:isMobile?9:10,color:C.t3,fontWeight:600,letterSpacing:.3,whiteSpace:'nowrap',lineHeight:1.1}}>{s.label}</span>
                      <span style={{fontSize:isMobile?12:14,fontWeight:700,color:s.color,fontVariantNumeric:'tabular-nums',lineHeight:1.15,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{s.val}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          );
        })()}
        {/* [UI-2026-05-19f] Compact tabs: narrower padding, lucide icons instead of emojis,
            smaller icon container so tab bar takes less vertical space. */}
        <div style={{display:'flex',background:isDark?'#0b1424':'#f5f7fb',padding:'4px 12px',gap:6,alignItems:'center',borderTop:`1px solid ${C.b1}`,borderBottom:`1px solid ${C.b1}`}}>
          {[
            ['o', tt('Открытые позиции'), ((activeTrades||[]).length + cfdPos.length), ClipboardList],
            ['h', tt('История'), ((tradeHistory||[]).length + cfdHist.length), ClockIcon]
          ].map(([t,lbl,cnt,TabIcon])=>{
            const isActive = posTab===t;
            return (
              <button key={t} onClick={()=>setPosTab(t)}
                data-testid={`pos-tab-${t}`}
                style={{
                  display:'inline-flex',alignItems:'center',gap:6,padding:'6px 12px',
                  fontSize:12,fontWeight:isActive?700:600,
                  border:isActive?`1px solid ${C.B}`:'1px solid transparent',
                  background:isActive?(isDark?'rgba(59,110,248,.18)':'rgba(59,110,248,.10)'):'transparent',
                  color:isActive?C.B:C.t2,cursor:'pointer',borderRadius:7,
                  transition:'all .12s'
                }}>
                <TabIcon size={14} strokeWidth={2.4}/>
                <span>{lbl}</span>
                {cnt > 0 && <span style={{padding:'1px 7px',borderRadius:9,fontSize:10,fontWeight:700,background:isActive?C.B:C.s3,color:isActive?'#fff':C.t3,minWidth:18,textAlign:'center'}}>{cnt}</span>}
              </button>
            );
          })}
        </div>
        <div style={{overflowX:'auto',WebkitOverflowScrolling:'touch',maxHeight:'min(34vh, 280px)',overflowY:'auto'}}>
          <table style={{minWidth:520,width:'100%',borderCollapse:'collapse',fontSize:11}}>
            <thead>
              <tr style={{background:C.s2}}>
                {/* [CFD-2026-05] Unified columns — opts and CFD combined */}
                {/* [i18n-2026-08-03] Wrapped strings with tt() so EN/RU switches propagate. */}
                {posTab==='o' && [tt('Актив'),tt('Тип'),tt('Направл.'),tt('Вход/Страйк'),tt('Тек./Сумма'),tt('П/У'),tt('Объём'),tt('Открыто'),tt('Доп.'),tt('Действие')].map(h=><th key={h} style={{padding:'5px 8px',textAlign:'left',fontSize:10,color:C.t3,fontWeight:600,borderBottom:`1px solid ${C.b1}`,whiteSpace:'nowrap'}}>{h}</th>)}
                {posTab==='h' && [tt('Актив'),tt('Тип'),tt('Направление'),tt('Сумма'),tt('Результат'),tt('Статус'),tt('Время')].map(h=><th key={h} style={{padding:'5px 8px',textAlign:'left',fontSize:10,color:C.t3,fontWeight:600,borderBottom:`1px solid ${C.b1}`,whiteSpace:'nowrap'}}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {posTab==='o' && (activeTrades||[]).length===0 && cfdPos.length===0 && <tr><td colSpan={10} style={{textAlign:'center',padding:12,color:C.t4,fontStyle:'italic'}}>{tt('Нет открытых позиций')}</td></tr>}
              {/* Active OPTIONS rows */}
              {posTab==='o' && (activeTrades||[]).map(t => {
                // [TP-2026-05-14] TradingEngine stores fields in camelCase (`expiresAt`, `strikePrice`).
                // Accept both shapes so the countdown timer ticks correctly.
                const expiryRaw = t.expires_at || t.expiresAt;
                const strike = t.strike_price ?? t.strikePrice ?? 0;
                const rem = expiryRaw ? Math.max(0, Math.ceil((new Date(expiryRaw) - Date.now()) / 1000)) : 0;
                const fmt = (s) => {
                  if (s <= 0) return tt('Обработка...');
                  const h = Math.floor(s / 3600);
                  const m = Math.floor((s % 3600) / 60);
                  const sec = s % 60;
                  if (h > 0) return `${h}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
                  return `${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
                };
                return <tr key={`opt-${t.id}`} style={{borderBottom:`1px solid ${C.s3}`,cursor:'pointer'}} onClick={()=>setSym(Object.values(MKT).flatMap(m=>m.syms).find(s=>s.s===t.asset)||sym)} data-testid={`opt-row-${t.id}`}>
                  <td style={{padding:'5px 8px',fontWeight:700,color:C.t1}}>{t.asset}</td>
                  <td style={{padding:'5px 8px',color:C.G,fontWeight:600}}>{tt('Опцион')}</td>
                  <td style={{padding:'5px 8px'}}>{dirBadge(t.direction==='call'?'c':'p')}</td>
                  <td style={{padding:'5px 8px',fontFamily:'monospace',color:C.B,fontWeight:700}}>{parseFloat(strike||0).toFixed(5)}</td>
                  <td style={{padding:'5px 8px'}}>${parseFloat(t.amount||0).toFixed(2)}</td>
                  <td style={{padding:'5px 8px',color:C.G}}>${parseFloat((t.expected_payout||t.amount*1.85)||0).toFixed(2)}</td>
                  <td style={{padding:'5px 8px',color:C.t3}}>—</td>
                  {/* [OPEN-TIME-2026-08-03] Показываем время открытия сделки */}
                  <td style={{padding:'5px 8px',color:C.t3,fontSize:10,fontVariantNumeric:'tabular-nums'}}>
                    {(t.created_at || t.createdAt) ? new Date(t.created_at || t.createdAt).toLocaleString('ru-RU',{day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'}) : '—'}
                  </td>
                  <td style={{padding:'5px 8px'}}>
                    <span data-testid={`opt-countdown-${t.id}`}
                      style={{background:rem>0?C.Ybg:C.s3,color:rem>0?C.Yt:C.t3,padding:'2px 7px',borderRadius:4,fontSize:10,fontWeight:700,border:`1px solid ${rem>0?C.Ybd:C.b2}`,fontVariantNumeric:'tabular-nums'}}>
                      {fmt(rem)}
                    </span>
                  </td>
                  <td style={{padding:'5px 8px',color:C.t4,fontSize:10}}>—</td>
                </tr>;
              })}
              {/* Active CFD rows */}
              {posTab==='o' && cfdPos.map(pos => {
                const pnl = getCFDPnl(pos);
                const cur = getCFDCurrentPrice(pos);
                const sl = pos.sl||pos.stop_loss, tp = pos.tp||pos.take_profit;
                return <tr key={`cfd-${pos.id}`} style={{borderBottom:`1px solid ${C.s3}`,cursor:'pointer'}} onClick={()=>{const s=Object.values(MKT).flatMap(m=>m.syms).find(s=>s.s===(pos.symbol||pos.sym));if(s)setSym(s);}}>
                  <td style={{padding:'5px 8px',fontWeight:700,color:C.t1}}>{pos.symbol||pos.sym}</td>
                  <td style={{padding:'5px 8px',color:C.B,fontWeight:600}}>CFD</td>
                  <td style={{padding:'5px 8px'}}>{dirBadge(pos.dir||pos.direction)}</td>
                  <td style={{padding:'5px 8px',fontFamily:'monospace',color:C.B,fontWeight:700}}>{parseFloat(pos.entry_price||pos.entry||0).toFixed(5)}</td>
                  <td style={{padding:'5px 8px',fontFamily:'monospace'}}>{parseFloat(cur||0).toFixed(5)}</td>
                  <td style={{padding:'5px 8px',fontWeight:700,color:pnl>=0?C.G:C.R}}>{pnl>=0?'+':''}${pnl.toFixed(2)}</td>
                  <td style={{padding:'5px 8px'}}>{parseFloat(pos.volume||pos.vol||0).toFixed(2)} {tt('лот')} · ${Math.round(pos.margin||0)}</td>
                  {/* [OPEN-TIME-2026-08-03] Время открытия CFD позиции */}
                  <td style={{padding:'5px 8px',color:C.t3,fontSize:10,fontVariantNumeric:'tabular-nums'}}>
                    {(pos.opened_at || pos.created_at) ? new Date(pos.opened_at || pos.created_at).toLocaleString('ru-RU',{day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'}) : '—'}
                  </td>
                  <td style={{padding:'5px 8px',fontSize:10}}>
                    {sl ? <span style={{color:C.Rt}}>SL {parseFloat(sl).toFixed(4)}</span> : <span style={{color:C.t4}}>SL —</span>}
                    <br/>
                    {tp ? <span style={{color:C.Gt}}>TP {parseFloat(tp).toFixed(4)}</span> : <span style={{color:C.t4}}>TP —</span>}
                  </td>
                  <td style={{padding:'5px 8px'}}><button onClick={e=>{e.stopPropagation();handleCloseCFD(pos);}} data-testid={`close-cfd-${pos.id}`} style={{padding:'3px 8px',fontSize:10,fontWeight:700,borderRadius:5,border:`1px solid ${C.Rbd}`,background:C.Rbg,color:C.Rt,cursor:'pointer'}}>{tt('Закрыть')}</button></td>
                </tr>;
              })}
              {/* History — unified opts + cfd */}
              {posTab==='h' && (tradeHistory||[]).length===0 && cfdHist.length===0 && <tr><td colSpan={7} style={{textAlign:'center',padding:12,color:C.t4,fontStyle:'italic'}}>{tt('История пуста')}</td></tr>}
              {posTab==='h' && [
                ...(tradeHistory||[]).map(h => ({ ...h, _kind:'opt', _ts:h.completed_at?new Date(h.completed_at).getTime():0 })),
                ...cfdHist.map(h => ({ ...h, _kind:'cfd', _ts:h.closed_at?new Date(h.closed_at).getTime():(h.ca?Date.parse(h.ca):0) })),
              ].sort((a,b) => (b._ts||0) - (a._ts||0)).slice(0,60).map((h,i) => {
                const isOpt = h._kind === 'opt';
                const won = isOpt ? (h.status==='won') : ((h.realized_pnl ?? h.pnl ?? 0) >= 0);
                const d = isOpt ? (h.direction==='call'?'c':'p') : (h.dir||h.direction);
                const amount = isOpt ? parseFloat(h.amount||0) : parseFloat(h.margin||h.volume*100||0);
                const res = isOpt
                  ? (won ? '+$'+parseFloat(h.profit_loss||0).toFixed(2) : '-$'+parseFloat(h.amount||0).toFixed(2))
                  : `${(h.realized_pnl ?? h.pnl ?? 0)>=0?'+':''}$${Math.abs(h.realized_pnl ?? h.pnl ?? 0).toFixed(2)}`;
                const time = isOpt
                  ? (h.completed_at?new Date(h.completed_at).toLocaleTimeString('ru-RU',{hour:'2-digit',minute:'2-digit'}):'—')
                  : (h.ca || (h.closed_at?new Date(h.closed_at).toLocaleTimeString('ru-RU',{hour:'2-digit',minute:'2-digit'}):'—'));
                return <tr key={`${h._kind}-${i}`} style={{borderBottom:`1px solid ${C.s3}`}}>
                  <td style={{padding:'5px 8px',fontWeight:700,color:C.t1}}>{h.asset||h.symbol||h.lbl}</td>
                  <td style={{padding:'5px 8px',color:isOpt?C.G:C.B,fontWeight:600}}>{isOpt?'Опцион':'CFD'}</td>
                  <td style={{padding:'5px 8px'}}>{dirBadge(d)}</td>
                  <td style={{padding:'5px 8px'}}>${amount.toFixed(2)}</td>
                  <td style={{padding:'5px 8px',fontWeight:700,color:won?C.G:C.R}}>{res}</td>
                  <td style={{padding:'5px 8px'}}>{statBadge(won)}</td>
                  <td style={{padding:'5px 8px',color:C.t3}}>{time}</td>
                </tr>;
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── SIDEBAR ── */}
      {sidebarOpen && (
        <div onClick={e=>{if(e.target===e.currentTarget)setSidebarOpen(false);}} style={{position:'fixed',inset:0,background:'rgba(0,0,0,.4)',zIndex:9999,display:'flex',justifyContent:'flex-end'}}>
          <div style={{width:220,background:C.s,borderLeft:`1px solid ${C.b1}`,height:'100%',overflowY:'auto',display:'flex',flexDirection:'column'}}>
            <div style={{padding:'11px 13px',borderBottom:`1px solid ${C.b1}`,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <span style={{fontSize:12,fontWeight:700,color:C.t1}}>{tt('Мой аккаунт')}</span>
              <button onClick={()=>setSidebarOpen(false)} style={{background:'none',border:'none',color:C.t3,fontSize:19,cursor:'pointer'}}>×</button>
            </div>
            <div style={{padding:'11px 13px',borderBottom:`1px solid ${C.b1}`,background:C.s2}}>
              <div style={{width:34,height:34,borderRadius:'50%',background:C.B,display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:700,color:'#fff',marginBottom:6}}>
                {(user?.name||'JD').split(' ').map(w=>w[0]).join('').substring(0,2).toUpperCase()}
              </div>
              <div style={{fontSize:12,fontWeight:700,color:C.t1}}>{user?.name||'John Doe'}</div>
              <div style={{fontSize:10,color:C.t3,marginTop:1}}>ID: {user?.client_id||user?.platform_id||user?.id}</div>
              <div style={{fontSize:14,fontWeight:700,color:C.G,marginTop:5,fontVariantNumeric:'tabular-nums'}}>${walletBalance.toFixed(2)}</div>
            </div>
            <div style={{padding:'6px',flex:1}}>
              {/* [CFD-2026-05] Mode switch in user menu */}
              <div style={{padding:'6px 8px',borderBottom:`1px solid ${C.b1}`,marginBottom:6}}>
                <div style={{fontSize:9,color:C.t3,fontWeight:700,letterSpacing:.5,marginBottom:5}}>{tt('РЕЖИМ ТОРГОВЛИ')}</div>
                <div style={{display:'flex',gap:4}}>
                  <button onClick={()=>{setMode('opt');setSidebarOpen(false);}} data-testid="menu-mode-opt" style={{flex:1,padding:'6px 4px',fontSize:11,fontWeight:600,borderRadius:5,border:`1px solid ${mode==='opt'?C.B:C.b1}`,background:mode==='opt'?C.B:C.s2,color:mode==='opt'?'#fff':C.t1,cursor:'pointer'}}>{tt('Опционы')}</button>
                  <button onClick={()=>{setMode('cfd');setSidebarOpen(false);}} data-testid="menu-mode-cfd" style={{flex:1,padding:'6px 4px',fontSize:11,fontWeight:600,borderRadius:5,border:`1px solid ${mode==='cfd'?C.B:C.b1}`,background:mode==='cfd'?C.B:C.s2,color:mode==='cfd'?'#fff':C.t1,cursor:'pointer'}}>CFD</button>
                </div>
              </div>
              {[
                {section:tt('Финансы'),items:[
                  {label:tt('Пополнение счёта'),act:()=>{setSidebarOpen(false);setShowDeposit(true);}},
                  {label:tt('Вывод средств'),act:()=>{setSidebarOpen(false);setShowWithdraw(true);}},
                ]},
                {section:tt('Аккаунт'),items:[
                  {label:tt('Сообщения'),badge: unreadMsg>0 ? String(unreadMsg) : null,act:()=>{setSidebarOpen(false);setShowMessages(true);}},
                  {label:tt('Документы'),act:()=>{setSidebarOpen(false);setShowDocuments(true);}},
                  {label:tt('Смена пароля'),act:()=>{setSidebarOpen(false);setShowChangePw(true);}},
                  {label:tt('Поддержка'),act:()=>{
                    setSidebarOpen(false);
                    setShowSupport(true);
                    if(!supportContacts){
                      axios.get(`${API}/support/contacts`).then(r=>setSupportContacts(r.data||{})).catch(()=>setSupportContacts({}));
                    }
                  }},
                  {label:tt('Скачать приложение'),act:async()=>{setSidebarOpen(false);if(window.installPWA){await window.installPWA();}else{showMsg(tt('Приложение уже установлено или недоступно'));}}},
                ]},
              ].map(section=>(
                <div key={section.section}>
                  <div style={{fontSize:9,fontWeight:700,color:C.t4,padding:'5px 8px 2px',letterSpacing:1,textTransform:'uppercase'}}>{section.section}</div>
                  {section.items.map(item=>(
                    <button key={item.label} onClick={item.act} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'7px 9px',borderRadius:6,cursor:'pointer',border:'none',background:'transparent',width:'100%',color:C.t2,fontSize:11,textAlign:'left',fontWeight:500,transition:'all .12s'}}
                      onMouseOver={e=>e.currentTarget.style.background=C.s2} onMouseOut={e=>e.currentTarget.style.background='transparent'}>
                      {item.label}
                      {item.badge&&<span style={{fontSize:9,padding:'2px 6px',borderRadius:10,background:C.R,color:'#fff',fontWeight:700}}>{item.badge}</span>}
                    </button>
                  ))}
                  <div style={{height:1,background:C.b1,margin:'3px 0'}}/>
                </div>
              ))}
              {/* [CFD-2026-05] Theme + Language switchers — moved here from top bar */}
              <div style={{padding:'8px 9px',marginTop:4}}>
                <div style={{fontSize:9,color:C.t3,fontWeight:700,letterSpacing:.4,textTransform:'uppercase',marginBottom:8}}>{tt('Настройки интерфейса')}</div>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'8px 0',gap:8}}>
                  <span style={{fontSize:11,color:C.t1,fontWeight:600,display:'flex',alignItems:'center',gap:8}}>
                    {isDark ? <Moon size={14}/> : <Sun size={14}/>}
                    {isDark ? tt('Тёмная тема') : tt('Светлая тема')}
                  </span>
                  <button onClick={()=>setIsDark(!isDark)} data-testid="sidebar-theme-toggle"
                    style={{width:38,height:22,borderRadius:11,border:'none',background:isDark?C.B:C.b2,position:'relative',cursor:'pointer',transition:'background .15s'}}>
                    <span style={{position:'absolute',top:2,left:isDark?18:2,width:18,height:18,borderRadius:'50%',background:'#fff',transition:'left .15s',boxShadow:'0 1px 3px rgba(0,0,0,.3)'}}/>
                  </button>
                </div>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'8px 0',gap:8}}>
                  <span style={{fontSize:11,color:C.t1,fontWeight:600}}>{tt('Язык')}</span>
                  <div style={{display:'flex',background:C.s2,border:`1px solid ${C.b1}`,borderRadius:8,padding:2}} data-testid="sidebar-lang-switcher">
                    {['ru','en'].map(L => (
                      <button key={L} onClick={()=>switchLang(L)} data-testid={`sidebar-lang-${L}`}
                        style={{padding:'4px 10px',fontSize:10,fontWeight:700,letterSpacing:.4,borderRadius:5,border:'none',cursor:'pointer',
                          background: lang===L ? 'linear-gradient(135deg, #6d4af0, #5b3fde)' : 'transparent',
                          color: lang===L ? '#fff' : C.t2}}>
                        {L.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{height:1,background:C.b1,margin:'4px 0'}}/>
              </div>
              <button onClick={()=>{logout?.();}} style={{display:'flex',alignItems:'center',padding:'7px 9px',borderRadius:6,cursor:'pointer',border:'none',background:'transparent',width:'100%',color:C.Rt,fontSize:11,textAlign:'left',fontWeight:500}}>
                {tt('Выйти из системы')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODALS */}
      {showDeposit  && <DepositModal  onClose={()=>setShowDeposit(false)} />}
      {showWithdraw && <WithdrawModal onClose={()=>setShowWithdraw(false)} balance={balance} />}
      {showMessages && (
        <div onClick={(e)=>{if(e.target===e.currentTarget)setShowMessages(false);}} style={{position:'fixed',inset:0,background:'rgba(0,0,0,.65)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} data-testid="messages-modal-overlay">
          <div style={{background:isDark?'#1f2937':'#fff',borderRadius:12,maxWidth:800,width:'100%',maxHeight:'85vh',overflow:'hidden',display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,.4)'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 18px',borderBottom:`1px solid ${C.b1}`}}>
              <h3 style={{margin:0,fontSize:16,fontWeight:700,color:C.t1}}>{tt('Сообщения')}</h3>
              <button onClick={()=>setShowMessages(false)} style={{background:'none',border:'none',fontSize:22,color:C.t3,cursor:'pointer',lineHeight:1}} aria-label="close">×</button>
            </div>
            <div style={{overflowY:'auto',flex:1,padding:0}}>
              <ClientMessages />
            </div>
          </div>
        </div>
      )}
      {/* [CFD-2026-05] */}
      {showDocuments && <DocumentsModal isOpen={true} onClose={()=>setShowDocuments(false)} />}
      {showChangePw && <ChangePasswordModal isOpen={true} onClose={()=>setShowChangePw(false)} user={user} />}
      {showSupport && (
        <div onClick={(e)=>{if(e.target===e.currentTarget)setShowSupport(false);}} style={{position:'fixed',inset:0,background:'rgba(0,0,0,.65)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} data-testid="support-modal-overlay">
          <div style={{background:isDark?'#1f2937':'#fff',borderRadius:12,maxWidth:420,width:'100%',padding:'22px 24px',boxShadow:'0 20px 60px rgba(0,0,0,.4)'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
              <h3 style={{margin:0,fontSize:17,fontWeight:700,color:C.t1}}>Поддержка</h3>
              <button onClick={()=>setShowSupport(false)} style={{background:'none',border:'none',fontSize:22,color:C.t3,cursor:'pointer',lineHeight:1}} aria-label="close">×</button>
            </div>
            <p style={{margin:'0 0 16px',fontSize:13,color:C.t3,lineHeight:1.5}}>
              Свяжитесь с нашей службой поддержки удобным способом
            </p>
            {supportContacts === null ? (
              <div style={{textAlign:'center',padding:20,color:C.t3,fontSize:13}}>Загрузка...</div>
            ) : (
              <div style={{display:'flex',flexDirection:'column',gap:10}}>
                {supportContacts.telegram && (
                  <a href={`https://t.me/${String(supportContacts.telegram).replace('@','')}`} target="_blank" rel="noopener noreferrer" data-testid="support-telegram"
                    style={{display:'flex',alignItems:'center',gap:12,padding:'12px 14px',borderRadius:9,background:'#0088cc',color:'#fff',textDecoration:'none',fontWeight:600,fontSize:14,transition:'transform .15s'}}
                    onMouseDown={(e)=>{e.currentTarget.style.transform='scale(0.98)';}}
                    onMouseUp={(e)=>{e.currentTarget.style.transform='scale(1)';}}>
                    <span style={{fontSize:22}}>✈️</span>
                    <span style={{flex:1}}>Telegram<br/><small style={{opacity:.85,fontWeight:400}}>{supportContacts.telegram}</small></span>
                    <span style={{opacity:.7}}>›</span>
                  </a>
                )}
                {supportContacts.whatsapp && (
                  <a href={`https://wa.me/${String(supportContacts.whatsapp).replace(/\D/g,'')}`} target="_blank" rel="noopener noreferrer" data-testid="support-whatsapp"
                    style={{display:'flex',alignItems:'center',gap:12,padding:'12px 14px',borderRadius:9,background:'#25d366',color:'#fff',textDecoration:'none',fontWeight:600,fontSize:14,transition:'transform .15s'}}
                    onMouseDown={(e)=>{e.currentTarget.style.transform='scale(0.98)';}}
                    onMouseUp={(e)=>{e.currentTarget.style.transform='scale(1)';}}>
                    <span style={{fontSize:22}}>💬</span>
                    <span style={{flex:1}}>WhatsApp<br/><small style={{opacity:.85,fontWeight:400}}>{supportContacts.whatsapp}</small></span>
                    <span style={{opacity:.7}}>›</span>
                  </a>
                )}
                {supportContacts.email && (
                  <a href={`mailto:${supportContacts.email}`} data-testid="support-email"
                    style={{display:'flex',alignItems:'center',gap:12,padding:'12px 14px',borderRadius:9,background:isDark?'#374151':'#f3f4f6',color:C.t1,textDecoration:'none',fontWeight:600,fontSize:14}}>
                    <span style={{fontSize:22}}>📧</span>
                    <span style={{flex:1}}>Email<br/><small style={{opacity:.7,fontWeight:400}}>{supportContacts.email}</small></span>
                  </a>
                )}
                {!supportContacts.telegram && !supportContacts.whatsapp && !supportContacts.email && (
                  <div style={{padding:14,background:isDark?'#374151':'#f3f4f6',borderRadius:8,fontSize:13,color:C.t3,textAlign:'center'}}>
                    Контакты поддержки не настроены
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* [CFD-2026-05] Mobile sidebar backdrop */}
      {isMobile && mobileSidebarOpen && (
        <div onClick={()=>setMobileSidebarOpen(false)} data-testid="mobile-nav-backdrop"
          style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:1400}}/>
      )}

      {/* [CFD-2026-05] Mobile bottom-sheet backdrop */}
      {isMobile && mobileTradePanel && (
        <div onClick={()=>setMobileTradePanel(false)} data-testid="mobile-trade-backdrop"
          style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:1300}}/>
      )}

      {/* [UX-2026-05-19e] Removed the mobile bottom-left "Открытые" FAB — it
          duplicated the dedicated "Открытые позиции" view available from the
          left nav and was visually intrusive when a trade was open. */}

      {/* [UI-2026-05-19f] Mobile market picker overlay — full-screen sheet
          showing all market categories and their assets so phone users can
          switch instruments without opening the navigation drawer. */}
      {isMobile && mobileMarketPicker && (
        <>
          <div onClick={()=>setMobileMarketPicker(false)}
            style={{position:'fixed',inset:0,background:'rgba(0,0,0,.55)',zIndex:1400}}/>
          <div data-testid="mobile-market-picker"
            style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:C.bg,zIndex:1401,
              display:'flex',flexDirection:'column'}}>
            <div style={{padding:'14px 16px',display:'flex',alignItems:'center',gap:10,borderBottom:`1px solid ${C.b1}`,background:C.s,flexShrink:0}}>
              <span style={{fontSize:16,fontWeight:800,color:C.t1,flex:1}}>{tt('Выбрать инструмент')}</span>
              <button onClick={()=>setMobileMarketPicker(false)} data-testid="mobile-market-picker-close"
                style={{background:'none',border:'none',color:C.t2,fontSize:28,cursor:'pointer',lineHeight:1,padding:'0 6px',touchAction:'manipulation'}}>×</button>
            </div>
            <div style={{padding:'10px 14px',display:'flex',gap:6,overflowX:'auto',background:C.s,borderBottom:`1px solid ${C.b1}`,flexShrink:0}}>
              <button onClick={()=>setCurMkt('fav')} data-testid="mobile-mkt-fav"
                style={{flexShrink:0,padding:'8px 14px',borderRadius:9,fontSize:12,fontWeight:700,cursor:'pointer',
                  border:`1.5px solid ${curMkt==='fav'?'#d4a017':C.b2}`,
                  background:curMkt==='fav'?'linear-gradient(135deg, #f6c742, #d4a017)':C.s2,
                  color:curMkt==='fav'?'#1a1a2e':C.t1}}>
                ★ {tt('Избранное')}{watchlist.length>0 && ` (${watchlist.length})`}
              </button>
              {Object.keys(MKT).map(k => (
                <button key={k} onClick={()=>setCurMkt(k)} data-testid={`mobile-mkt-${k}`}
                  style={{flexShrink:0,padding:'8px 14px',borderRadius:9,fontSize:12,fontWeight:700,cursor:'pointer',
                    border:`1.5px solid ${curMkt===k?C.B:C.b2}`,
                    background:curMkt===k?`linear-gradient(135deg, ${C.B}, ${isDark?'#5b3fde':'#3b6ef8'})`:C.s2,
                    color:curMkt===k?'#fff':C.t1}}>
                  {tt(MKT[k].label)}
                </button>
              ))}
            </div>
            <div style={{flex:1,overflowY:'auto',padding:'6px 10px'}}>
              {(curMkt==='fav'
                ? Object.values(MKT).flatMap(m=>m.syms).filter(s=>watchlist.includes(s.s))
                : (MKT[curMkt]?.syms || [])
              ).map(s => {
                const p = prices[s.s]||s.p;
                const chg = ((p-BASE_PRICES[s.s])/BASE_PRICES[s.s]*100).toFixed(2);
                const up = parseFloat(chg)>=0;
                const active = s.s===sym.s;
                return (
                  <button key={s.s}
                    onClick={()=>{ setSym(s); setDraws([]); setDrawNow(null); setMobileMarketPicker(false); }}
                    data-testid={`mobile-asset-${s.s}`}
                    style={{display:'flex',alignItems:'center',gap:12,padding:'13px 12px',borderRadius:10,cursor:'pointer',width:'100%',
                      border:`1.5px solid ${active?C.B:'transparent'}`,
                      background: active?(isDark?'rgba(124,92,246,.18)':'rgba(124,92,246,.10)'):C.s,
                      marginBottom:5, textAlign:'left', touchAction:'manipulation'}}>
                    <FlagIcon symbol={s.s} size={28}/>
                    <div style={{display:'flex',flexDirection:'column',flex:1,gap:2,minWidth:0}}>
                      <span style={{fontSize:14,fontWeight:700,color:C.t1}}>{s.l}</span>
                      <span style={{fontSize:11,color:C.t3}}>{s.s}</span>
                    </div>
                    <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:2}}>
                      <span style={{fontSize:13,fontWeight:700,color:C.t1,fontVariantNumeric:'tabular-nums'}}>{p.toFixed(s.d)}</span>
                      <span style={{fontSize:11,fontWeight:700,color:up?C.G:C.R,fontVariantNumeric:'tabular-nums'}}>{up?'+':''}{chg}%</span>
                    </div>
                  </button>
                );
              })}
              {curMkt==='fav' && watchlist.length===0 && (
                <div style={{textAlign:'center',color:C.t3,padding:'40px 20px',fontSize:13}}>
                  {tt('Список избранного пуст. Откройте инструмент и нажмите ★, чтобы добавить.')}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* [FAB-2026-05-28e] Draggable BUY/SELL FAB — clean SVG design matching platform style. */}
      {isMobile && !mobileTradePanel && navView==='chart' && (
        <button
          data-testid="mobile-trade-fab"
          aria-label="Открыть сделку"
          onMouseDown={(e)=>{ fabStart(e.clientX, e.clientY); }}
          onTouchStart={(e)=>{ const t=e.touches[0]; fabStart(t.clientX, t.clientY); }}
          onClick={()=>{
            if (fabDragRef.current.moved) return; // suppress click after drag
            setMobileTradePanel(true);
          }}
          style={{position:'fixed', top:fabPos.y, left:fabPos.x, width:FAB_SIZE, height:FAB_SIZE,
            borderRadius:'50%', padding:0, border:`2px solid ${isDark?'rgba(255,255,255,.08)':'rgba(0,0,0,.08)'}`,
            background:'transparent', overflow:'hidden',
            cursor:'grab', zIndex:900, touchAction:'none', userSelect:'none',
            boxShadow:'0 10px 28px rgba(0,0,0,.55), 0 0 0 1px rgba(0,0,0,.4)'}}>
          <svg viewBox="0 0 64 64" width="100%" height="100%" style={{display:'block', pointerEvents:'none'}}>
            <defs>
              <linearGradient id="fabBuyGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#22d39a"/>
                <stop offset="100%" stopColor="#0f9466"/>
              </linearGradient>
              <linearGradient id="fabSellGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f87171"/>
                <stop offset="100%" stopColor="#b91c1c"/>
              </linearGradient>
              <radialGradient id="fabShine" cx="50%" cy="20%" r="80%">
                <stop offset="0%" stopColor="#fff" stopOpacity=".22"/>
                <stop offset="60%" stopColor="#fff" stopOpacity="0"/>
              </radialGradient>
            </defs>
            {/* Left half — BUY (green) */}
            <path d="M32 0 A32 32 0 0 0 32 64 L32 0 Z" fill="url(#fabBuyGrad)"/>
            {/* Right half — SELL (red) */}
            <path d="M32 0 A32 32 0 0 1 32 64 L32 0 Z" fill="url(#fabSellGrad)"/>
            {/* Glossy highlight overlay */}
            <circle cx="32" cy="32" r="31" fill="url(#fabShine)"/>
            {/* Center vertical divider */}
            <line x1="32" y1="6" x2="32" y2="58" stroke="rgba(0,0,0,.28)" strokeWidth="1"/>
            {/* Up arrow — BUY side */}
            <path d="M17 30 L17 22 M13 26 L17 22 L21 26"
              stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            {/* Down arrow — SELL side */}
            <path d="M47 22 L47 30 M43 26 L47 30 L51 26"
              stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            {/* Labels */}
            <text x="17" y="46" textAnchor="middle" fontFamily="-apple-system, system-ui, sans-serif"
              fontSize="9.5" fontWeight="800" fill="#fff" letterSpacing=".4">BUY</text>
            <text x="47" y="46" textAnchor="middle" fontFamily="-apple-system, system-ui, sans-serif"
              fontSize="9.5" fontWeight="800" fill="#fff" letterSpacing=".4">SELL</text>
          </svg>
        </button>
      )}

      {/* [OPT-2026-05-13] Mobile-only global toast for trade notifications — always visible above all panels */}
      {isMobile && notif && (
        <div data-testid="mobile-notif-toast"
          style={{position:'fixed',top:74,left:'50%',transform:'translateX(-50%)',zIndex:2000,
            minWidth:'min(92vw, 360px)',maxWidth:'92vw',
            padding:'12px 16px',borderRadius:12,fontSize:13,fontWeight:600,
            background:notif.type==='ok'?C.Gbg:C.Rbg,color:notif.type==='ok'?C.Gt:C.Rt,
            border:`1px solid ${notif.type==='ok'?C.G:C.R}`,
            boxShadow:'0 12px 40px rgba(0,0,0,.35)',
            display:'flex',alignItems:'center',gap:10,
            animation:'slideDownToast .25s ease-out'}}>
          <div style={{width:26,height:26,borderRadius:'50%',background:notif.type==='ok'?C.G:C.R,color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,fontWeight:800,fontSize:14}}>
            {notif.type==='ok'?'✓':'!'}
          </div>
          <div style={{flex:1,minWidth:0}}>{notif.text}</div>
          <button onClick={()=>setNotif(null)} data-testid="mobile-notif-close"
            style={{background:'transparent',border:'none',color:'inherit',fontSize:18,cursor:'pointer',padding:0,lineHeight:1,opacity:.7}}>×</button>
        </div>
      )}

      {showCalendar && (
        <div onClick={(e)=>{if(e.target===e.currentTarget)setShowCalendar(false);}} data-testid="calendar-modal-overlay"
          style={{position:'fixed',inset:0,background:'rgba(0,0,0,.65)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
          <div style={{background:C.s,borderRadius:16,padding:0,width:'min(900px, 96vw)',maxHeight:'90vh',overflow:'hidden',display:'flex',flexDirection:'column',boxShadow:'0 16px 50px rgba(0,0,0,.4)'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 18px',borderBottom:`1px solid ${C.b1}`}}>
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                <CalendarDays size={20} color={C.B}/>
                <h3 style={{margin:0,fontSize:16,fontWeight:700,color:C.t1}}>Экономический календарь</h3>
              </div>
              <button onClick={()=>setShowCalendar(false)} data-testid="calendar-close-btn" style={{background:'none',border:'none',fontSize:24,color:C.t3,cursor:'pointer',lineHeight:1,padding:4}}>×</button>
            </div>
            <div style={{flex:1,overflow:'auto'}}>
              <EconomicCalendar />
            </div>
          </div>
        </div>
      )}
      {/* [CFD-2026-05-24] Trade confirmation modal — prevents accidental orders. */}
      {confirmTrade && (
        <div onClick={(e)=>{if(e.target===e.currentTarget)setConfirmTrade(null);}} data-testid="confirm-trade-overlay"
          style={{position:'fixed',inset:0,background:'rgba(0,0,0,.65)',zIndex:10000,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <div data-testid="confirm-trade-modal"
            style={{background:C.s,borderRadius:14,width:'min(420px, 96vw)',boxShadow:'0 16px 50px rgba(0,0,0,.5)',border:`1px solid ${C.b1}`,overflow:'hidden'}}>
            <div style={{padding:'14px 18px',borderBottom:`1px solid ${C.b1}`,display:'flex',alignItems:'center',gap:10,background:confirmTrade.dir==='b'||confirmTrade.dir==='c'?C.Gbg:C.Rbg}}>
              <span style={{fontSize:22}}>{confirmTrade.dir==='b'||confirmTrade.dir==='c'?'▲':'▼'}</span>
              <div style={{flex:1}}>
                <div style={{fontSize:15,fontWeight:800,color:confirmTrade.dir==='b'||confirmTrade.dir==='c'?C.Gt:C.Rt}}>
                  {confirmTrade.type==='cfd'
                    ? (confirmTrade.dir==='b'?tt('Покупка'):tt('Продажа'))
                    : (confirmTrade.dir==='c'?tt('ВЫШЕ'):tt('НИЖЕ'))} · {confirmTrade.label}
                </div>
                <div style={{fontSize:11,color:C.t3,fontWeight:600}}>
                  {confirmTrade.type==='cfd'?tt('Подтверждение CFD сделки'):tt('Подтверждение опциона')}
                </div>
              </div>
              <button onClick={()=>setConfirmTrade(null)} data-testid="confirm-trade-close"
                style={{background:'none',border:'none',fontSize:22,color:C.t3,cursor:'pointer',lineHeight:1,padding:4}}>×</button>
            </div>
            <div style={{padding:'14px 18px',display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,fontSize:12}}>
              {confirmTrade.type==='cfd' ? (
                <>
                  <div><div style={{color:C.t3,fontWeight:600,fontSize:10}}>{tt('Цена входа')}</div>
                    <div data-testid="confirm-entry" style={{color:C.t1,fontWeight:700,fontVariantNumeric:'tabular-nums'}}>{parseFloat(confirmTrade.entry||0).toFixed(confirmTrade.digits||5)}</div></div>
                  <div><div style={{color:C.t3,fontWeight:600,fontSize:10}}>{tt('Объём')}</div>
                    <div data-testid="confirm-volume" style={{color:C.t1,fontWeight:700,fontVariantNumeric:'tabular-nums'}}>{confirmTrade.vol}</div></div>
                  <div><div style={{color:C.t3,fontWeight:600,fontSize:10}}>{tt('Маржа')}</div>
                    <div data-testid="confirm-margin" style={{color:C.t1,fontWeight:700,fontVariantNumeric:'tabular-nums'}}>${confirmTrade.margin}</div></div>
                  <div><div style={{color:C.t3,fontWeight:600,fontSize:10}}>{tt('Плечо')}</div>
                    <div data-testid="confirm-leverage" style={{color:C.t1,fontWeight:700,fontVariantNumeric:'tabular-nums'}}>1:{confirmTrade.leverage}</div></div>
                  {confirmTrade.sl && <div><div style={{color:C.t3,fontWeight:600,fontSize:10}}>Stop Loss</div>
                    <div data-testid="confirm-sl" style={{color:C.Rt,fontWeight:700,fontVariantNumeric:'tabular-nums'}}>{parseFloat(confirmTrade.sl).toFixed(confirmTrade.digits||5)}</div></div>}
                  {confirmTrade.tp && <div><div style={{color:C.t3,fontWeight:600,fontSize:10}}>Take Profit</div>
                    <div data-testid="confirm-tp" style={{color:C.Gt,fontWeight:700,fontVariantNumeric:'tabular-nums'}}>{parseFloat(confirmTrade.tp).toFixed(confirmTrade.digits||5)}</div></div>}
                </>
              ) : (
                <>
                  <div><div style={{color:C.t3,fontWeight:600,fontSize:10}}>{tt('Сумма')}</div>
                    <div data-testid="confirm-amount" style={{color:C.t1,fontWeight:700,fontVariantNumeric:'tabular-nums'}}>${confirmTrade.amount}</div></div>
                  <div><div style={{color:C.t3,fontWeight:600,fontSize:10}}>{tt('Страйк')}</div>
                    <div data-testid="confirm-strike" style={{color:C.t1,fontWeight:700,fontVariantNumeric:'tabular-nums'}}>{parseFloat(confirmTrade.strike||0).toFixed(confirmTrade.digits||5)}</div></div>
                  <div><div style={{color:C.t3,fontWeight:600,fontSize:10}}>{tt('Срок')}</div>
                    <div data-testid="confirm-duration" style={{color:C.t1,fontWeight:700,fontVariantNumeric:'tabular-nums'}}>{Math.floor(confirmTrade.duration/60)}:{String(confirmTrade.duration%60).padStart(2,'0')}</div></div>
                  <div><div style={{color:C.t3,fontWeight:600,fontSize:10}}>{tt('Выплата')}</div>
                    <div style={{color:C.Gt,fontWeight:700,fontVariantNumeric:'tabular-nums'}}>~${(confirmTrade.amount*1.85).toFixed(2)}</div></div>
                </>
              )}
            </div>
            <div style={{padding:'12px 18px 16px',display:'flex',gap:10,borderTop:`1px solid ${C.b1}`}}>
              <button onClick={()=>setConfirmTrade(null)} data-testid="confirm-trade-cancel"
                style={{flex:1,padding:'12px 14px',fontSize:13,fontWeight:700,borderRadius:9,border:`1px solid ${C.b2}`,background:'transparent',color:C.t2,cursor:'pointer'}}>
                {tt('Отмена')}
              </button>
              <button data-testid="confirm-trade-confirm"
                onClick={async () => {
                  const c = confirmTrade;
                  setConfirmTrade(null);
                  if (c.type === 'cfd') {
                    await executeOpenCFD({ entry: c.entry, slPrice: c.sl, tpPrice: c.tp });
                  } else {
                    await executeOpenOption({ symLabel: c.label, dir: c.dir, amount: c.amount, lockedStrike: c.strike, duration: c.duration });
                  }
                }}
                style={{flex:1,padding:'12px 14px',fontSize:13,fontWeight:800,borderRadius:9,border:'none',color:'#fff',cursor:'pointer',
                  background: confirmTrade.dir==='b'||confirmTrade.dir==='c' ? 'linear-gradient(135deg, #10b981, #059669)' : 'linear-gradient(135deg, #ef4444, #dc2626)'}}>
                {tt('Подтвердить')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
function MarketBar({ MKT, curMkt, curSym, prices, base, onSelect, C }) {
  const [openDD, setOpenDD] = useState(null);
  useEffect(() => {
    const h = () => setOpenDD(null);
    document.addEventListener('click', h);
    return () => document.removeEventListener('click', h);
  }, []);
  return (
    <div style={{height:34,background:C.s,borderBottom:`1px solid ${C.b1}`,display:'flex',alignItems:'center',padding:'0 6px',gap:2,position:'relative',zIndex:30,flexShrink:0}}>
      {Object.entries(MKT).map(([k,m],i) => (
        <React.Fragment key={k}>
          {i>0 && <div style={{width:1,height:12,background:C.b1,margin:'0 2px'}}/>}
          <div style={{position:'relative'}}>
            <button onClick={e=>{e.stopPropagation();setOpenDD(openDD===k?null:k);}} style={{padding:'3px 9px',fontSize:11,fontWeight:k===curMkt?600:500,color:k===curMkt?C.t1:C.t3,background:k===curMkt?C.s3:'transparent',border:k===curMkt?`1px solid ${C.b2}`:'1px solid transparent',borderRadius:5,cursor:'pointer',height:26,display:'flex',alignItems:'center',gap:3,transition:'all .12s',whiteSpace:'nowrap'}}>
              {m.label}
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" style={{flexShrink:0,transform:openDD===k?'rotate(180deg)':'none',transition:'transform .15s'}}><path d="M2 3.5l3 3 3-3"/></svg>
            </button>
            {openDD===k && (
              <div onClick={e=>e.stopPropagation()} style={{position:'absolute',top:'calc(100% + 4px)',left:0,background:C.s,border:`1px solid ${C.b2}`,borderRadius:10,minWidth:195,padding:5,zIndex:200,boxShadow:'0 8px 28px rgba(0,0,0,.13)'}}>
                {m.syms.map(s => {
                  const p=prices[s.s]||s.p, chg=((p-base[s.s])/base[s.s]*100).toFixed(2), up=parseFloat(chg)>=0;
                  return <button key={s.s} onClick={()=>{onSelect(k,s);setOpenDD(null);}} style={{padding:'6px 9px',borderRadius:5,cursor:'pointer',fontSize:11,fontWeight:500,color:s.s===curSym.s&&k===curMkt?C.Bt:C.t2,display:'flex',justifyContent:'space-between',alignItems:'center',border:'none',background:s.s===curSym.s&&k===curMkt?C.Bbg:'transparent',width:'100%',textAlign:'left',transition:'all .12s'}}>
                    <span>{s.l}</span>
                    <span style={{display:'flex',alignItems:'center',gap:5,fontSize:10,fontVariantNumeric:'tabular-nums'}}>
                      <span style={{color:C.t3}}>{p.toFixed(s.d)}</span>
                      <span style={{color:up?C.G:C.R,fontWeight:700}}>{up?'+':''}{chg}%</span>
                    </span>
                  </button>;
                })}
              </div>
            )}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

function TFButton({ label, active, onClick, C }) {
  return (
    <button onClick={onClick} className={active?'tfb-active':''} style={{padding:'2px 7px',fontSize:10,fontWeight:active?700:500,borderRadius:3,border:active?`1px solid ${C.b2}`:'1px solid transparent',background:active?C.s3:'transparent',color:active?C.t1:C.t3,cursor:'pointer',height:20,transition:'all .12s'}} data-testid={`tf-btn-${label}`}>
      {label}
    </button>
  );
}

function ToolBtn({ t, active, onClick, C }) {
  return (
    <button onClick={onClick} title={t.title} data-testid={`tool-btn-${t.id}`} style={{width:26,height:26,display:'flex',alignItems:'center',justifyContent:'center',borderRadius:5,border:active?`1px solid ${C.B}`:'1px solid transparent',background:active?C.Bbg:'transparent',color:active?C.B:C.t1,cursor:'pointer',transition:'all .12s',padding:0}}>
      {t.svg}
    </button>
  );
}

export default TradingPlatform;
